/**
 * # dxfBridge.conformantSkeleton (DXF-CONFORMANT-SKELETON)
 *
 * Make a FROM-SCRATCH DrawingDocument (File → New, then draw — never imported
 * from a DXF) export an AutoCAD/ezdxf-conformant DXF.
 *
 * ## The problem
 *
 * The bridge export (`exportDrawingDocumentToDxfDocument`) is a PURE
 * carry-forward writer: it emits HEADER / CLASSES / VPORT / APPID / VIEW / UCS /
 * BLOCK_RECORD / BLOCKS / OBJECTS sections ONLY when the source document carries
 * the corresponding verbatim raw groups (`rawPassthroughSections`,
 * `rawTableGroups`, `rawObjectGroups`, per-block raw groups). A blank document
 * built by `buildInitialGeometryDocument` carries NONE of those, so its export
 * omits the HEADER (`$ACADVER` / `$HANDSEED`), the CLASSES section, the
 * VPORT / VIEW / UCS / APPID tables, the BLOCK_RECORD table (`*Model_Space` /
 * `*Paper_Space`), the BLOCKS section and the OBJECTS root DICTIONARY. On top of
 * that, entity handles (group 5) come from `entity.id` (`"line-1"`) which is
 * NON-HEX, so a strict `ezdxf.readfile` fails immediately with
 * `Invalid handle line-1`.
 *
 * ## The strategy (reuse the app's own round-trip capture)
 *
 * The app's PARSER already captures a conformant imported DXF into raw groups,
 * and the carry-forward writer re-emits them faithfully (validated by an ezdxf
 * strict round-trip of `CONFORMANT_BLANK_SKELETON_DXF`). We exploit that: parse
 * the embedded conformant blank skeleton once (memoized) and BACK-FILL its
 * structural sections onto the user's from-scratch document, then re-mint every
 * entity handle to hex.
 *
 * `ensureConformantSkeleton(document)`:
 *  - Returns IMPORTED documents UNCHANGED (byte-faithful carry-forward must not
 *    be disturbed — detection: any of `rawPassthroughSections` /
 *    `rawTableGroups` / `rawObjectGroups` present).
 *  - For a from-scratch document, returns a PURE CLONE (no input mutation —
 *    editorStore holds the document live) that:
 *      • adopts the skeleton's `rawPassthroughSections` (HEADER + CLASSES),
 *        `rawTableGroups` (VPORT / VIEW / UCS / APPID), `rawObjectGroups` (root
 *        DICTIONARY + the ACAD_* named-object dictionaries + Model/Layout1
 *        LAYOUT objects + MATERIAL / MLINESTYLE / MLEADERSTYLE / …),
 *      • adopts the skeleton's `blockRecords` + `blocks`
 *        (`*Model_Space` / `*Paper_Space`) and `layouts` (Model / Layout1) so the
 *        OBJECTS LAYOUT records have matching owning BLOCK_RECORDs,
 *      • KEEPS the user's `styleTables` (their layer "0" + Standard text/dim
 *        style), `linetypes` and `spaces` (their drawn entities) — the skeleton's
 *        LAYER / STYLE / DIMSTYLE / LTYPE / BLOCK_RECORD are TYPED on import, so
 *        adopting only the raw VPORT/VIEW/UCS/APPID groups never double-emits a
 *        second layer "0" or Standard style,
 *      • re-mints every entity's handle (`entity.id`) to a fresh uppercase-hex
 *        handle from a high base (0x100000+), collision-checked against the
 *        skeleton's used handles, and wires each entity's `ownerHandle` (DXF 330)
 *        to the `*Model_Space` / `*Paper_Space` BLOCK_RECORD handle,
 *      • bumps the HEADER `$HANDSEED` above the maximum handle in use.
 *
 * Source of truth: Autodesk DXF reference + ezdxf 1.4.3 reference output.
 */
import {
  OWN_HANDLE_GROUP_CODES,
  collectHandlesDeep,
  makeHandleMinter,
} from "./dxfBridge.annotativeExport";
import { isLayoutBlockName } from "./blockNaming";
import {
  dropDanglingCarriedPointers,
  mergeCarriedObjectRecords,
  remapCarriedPointers,
  type CarriedObjectRecord,
} from "./dxfBridge.carriedObjectRecords";
import {
  assignAnonymousDimensionBlocks,
  takeAnonymousDimensionBlockName,
  type AnonymousDimensionBlockAssignment,
} from "./dxfBridge.conformantSkeleton.dimBlocks";
import {
  OWN_HANDLE_KEYS,
  isHexHandle,
  remapEntityHandleReferences,
  remintRawEntityGroupHandles,
  remintRecordOwnHandles,
  rewriteEntityHandlesGeneric,
  rewriteRawEntityGroupRefs,
} from "./dxfBridge.conformantSkeleton.handles";
import {
  collectLayerPointerTargets,
  findMaterialHandleByName,
  findPlotStyleNormalHandle,
  readSkeletonTableHeadHandles,
  wireSymbolTableOwners,
} from "./dxfBridge.conformantSkeleton.tableOwners";
import { emittedBlockEntries } from "./dxfBridge.conformantSkeleton.emittedEntities";
import { materializeReferencedTextStyles } from "./dxfBridge.conformantSkeleton.textStyles";
import { maxHexHandleValue } from "./dxfBridge.handleSeed";
import { importDxfStringToDrawingDocument } from "./dxfBridge.import";
import { buildDefaultLayout } from "./dxfBridge.layouts";
import { CONFORMANT_BLANK_SKELETON_DXF } from "./dxfBridge.conformantSkeleton.template";
import { emitsTerminatingSeqend } from "./entityRecordIdentity";
import type {
  BlockDefinition,
  BlockRecord,
  DrawingDocument,
  DrawingEntity,
  DrawingSpace,
  Layout,
  RawEntityGroup,
} from "./model";

/** Local structural shape of a raw entity group (FIX C raw-handle remint). */
type RawEntityGroupLike = {
  entityType: string;
  groups: { code: number; value: string }[];
};

// Re-export the by-construction handle machinery so the census-gate test and any
// downstream consumer import from the conformant-skeleton barrel.
export {
  HANDLE_FIELD_CLASS,
  type HandleFieldClass,
  type HandleFieldName,
  isHexHandle,
  rewriteEntityHandlesGeneric,
  remapEntityHandleReferences,
  remintRecordOwnHandles,
} from "./dxfBridge.conformantSkeleton.handles";

/**
 * AutoCAD `*Model_Space` / `*Paper_Space` BLOCK_RECORD names — the owners of
 * model- and paper-space ENTITIES respectively (DXF group 330).
 */
const MODEL_SPACE_BLOCK_RECORD = "*Model_Space";
const PAPER_SPACE_BLOCK_RECORD = "*Paper_Space";

/**
 * Return only the USER-named entries of a name→record map, dropping every
 * RESERVED layout space-block name (`*Model_Space` / `*Paper_Space` /
 * `*Paper_Space<N>` — Axis C). When merging an INSERT-folded imported wrapper's
 * `blocks` / `blockRecords` over the skeleton, an entry under any of those names
 * carries the IMPORTED source handle and would clobber the skeleton record
 * `ownerBySpaceId` was wired from → a dangling owner (defect #6). The canonical
 * {@link isLayoutBlockName} predicate (case-insensitive, covers ALL paper
 * suffixes incl. `*Paper_Space1+` which the old hand-listed Set missed; user
 * blocks can never be `*`-prefixed so it is false-positive-safe) is the single
 * source of truth so only USER-named records merge over the skeleton.
 */
function userNamedRecords<T>(records: Record<string, T>): Record<string, T> {
  const out: Record<string, T> = {};
  for (const [name, record] of Object.entries(records)) {
    if (isLayoutBlockName(name)) continue;
    out[name] = record;
  }
  return out;
}

/** The skeleton's model-space LAYOUT object name (always present in a blank
 *  R2018 doc). */
const MODEL_LAYOUT_NAME = "Model";

/** The skeleton's single paper-space LAYOUT object name (`Layout1`, no space).
 *  It is owned by the skeleton's `*Paper_Space` BLOCK_RECORD (layoutHandle 340).
 *  When the document HAS user paper SPACES they replace it; when the document is
 *  model-ONLY (Codex P2 #3) it is KEPT so `*Paper_Space`'s layoutHandle resolves
 *  to a real LAYOUT (AutoCAD always needs ≥1 paper layout). */
const SKELETON_PAPER_LAYOUT_NAME = "Layout1";

/** First entity-handle value (exclusive of) the re-minter draws from. Above the
 * skeleton's low handles (≤ ~0x100), below the MLEADER (0x100000) range top so
 * it never overlaps the structural records. The downstream MLEADER / annotative
 * minters collision-check against the document-wide emitted-handle population
 * that already includes these reminted handles, so there is no cross-minter
 * collision. */
const ENTITY_HANDLE_BASE = 0x100000;

/**
 * Memoized parse of the embedded conformant skeleton. The parse is pure and the
 * result is treated as read-only (we `structuredClone` the structural fields we
 * adopt, never mutate the cached document).
 */
let cachedSkeleton: DrawingDocument | undefined;
function getSkeletonDocument(): DrawingDocument {
  if (cachedSkeleton === undefined) {
    cachedSkeleton = importDxfStringToDrawingDocument(
      CONFORMANT_BLANK_SKELETON_DXF,
    );
  }
  return cachedSkeleton;
}

/**
 * True when `document` is IMPORTED-SOURCE — i.e. it carries byte-faithful raw
 * DXF state that the carry-forward writer must emit VERBATIM (never re-mint /
 * reconstruct over). This is the SINGLE SOURCE OF TRUTH for the imported ⇔
 * from-scratch discrimination, consulted by BOTH {@link isFromScratchDocument}
 * (which inverts it) AND the export.ts ATTDEF de-dup (round-16 BUG 2 — the
 * de-dup must suppress the TYPED projection in favour of the byte-faithful RAW
 * ATTDEF for ANY imported-source doc, not only `provenance === "imported"`).
 *
 * AUTHORITATIVE signal: the in-memory `provenance` marker
 * (`importDxfStringToDrawingDocument` sets `provenance === "imported"` on every
 * parsed-DXF document; from-scratch builders never set it). SECONDARY by-
 * construction signal (round-16 sibling): a doc that carries ANY imported raw
 * SECTION source — `rawPassthroughSections` (HEADER/CLASSES), `rawTableGroups`
 * (VPORT/VIEW/UCS/APPID) or `rawObjectGroups` (root DICTIONARY + ACAD_* dicts) —
 * is imported-source even when the `provenance` marker was DROPPED somewhere in a
 * merge / open / clone / persist path. The from-scratch builders never populate
 * those verbatim group streams (the parser captures them ONLY from a parsed DXF),
 * so their presence is a sound "imported-source" signal. Hardens against any
 * path that rebuilds a doc from an imported source but loses the in-memory
 * marker → without it the skeleton would be injected + the source handles
 * re-minted over imported bytes (data loss), AND the export ATTDEF de-dup would
 * take the from-scratch branch (suppress the byte-faithful raw ATTDEF + emit a
 * reconstructed typed one → unknown ATTDEF groups lost, pass-through broken).
 *
 * round-14 BUG 2 (NOT-APPLICABLE rationale, kept as a guard against re-adding an
 * UNSOUND content signal):
 *   `document.rawEntityGroups` / `block.rawEntityGroups` are DELIBERATELY EXCLUDED
 *   from this predicate. They are NOT importer-exclusive: a FROM-SCRATCH document
 *   legitimately carries BOTH a top-level `document.rawEntityGroups` (a
 *   user-authored unsupported ENTITIES sibling) AND `block.rawEntityGroups` (a
 *   BEDIT raw block sibling), and such a doc MUST still be reminted — see the
 *   from-scratch reference tests `FIX C — raw entity-group own-handles` and
 *   `buildSentinelDocument`, which plant non-hex / skeleton-colliding raw own
 *   handles expecting the from-scratch remint to fix them. Flipping on
 *   rawEntityGroups would PASS those docs through verbatim → non-hex / duplicate
 *   group-5 → strict-ezdxf reject (the exact opposite of conformance). The
 *   `provenance:"imported"` marker is set UNCONDITIONALLY by the importer on EVERY
 *   parsed DXF (incl. ENTITIES-only), so a real imported doc never reaches the
 *   secondary signal without it. There is no content-only signal that separates
 *   imported-with-raw-entities from from-scratch-with-raw-entities, so no
 *   rawEntityGroups guard is added here.
 */
export function isImportedSourceDocument(document: DrawingDocument): boolean {
  // Marker present ⇒ imported document.
  if (document.provenance === "imported") return true;
  // Secondary by-construction guard — any imported raw SECTION source (NOT
  // rawEntityGroups, see the round-14 rationale above) signals imported-source
  // even when the marker was dropped in a merge/open/clone/persist path.
  return (
    (document.rawPassthroughSections?.length ?? 0) > 0 ||
    (document.rawTableGroups?.length ?? 0) > 0 ||
    (document.rawObjectGroups?.length ?? 0) > 0
  );
}

/**
 * True when `document` was NOT imported from a DXF (File → New).
 *
 * The exact inverse of {@link isImportedSourceDocument} (the SINGLE SOURCE OF
 * TRUTH for the discrimination). Marker present OR imported raw SECTION source ⇒
 * imported (return false, pass-through); neither ⇒ from-scratch (return true,
 * back-fill). The previous content-only heuristic was unsound in BOTH directions:
 *   - FALSE NEGATIVE: a minimal ENTITIES-only import carries no raw passthrough
 *     sections, no rawTable/rawObject groups, and no BLOCK_RECORD/BLOCK entries
 *     (the parser does NOT seed `*Model_Space`/`*Paper_Space` when the source
 *     has no BLOCKS section — `importBlockRecords` returns early on a null
 *     blocksSection). It was mis-detected as from-scratch → the skeleton was
 *     injected + the source handles re-minted, corrupting the imported
 *     round-trip.
 *   - FALSE POSITIVE: a from-scratch doc gains `blocks`/`blockRecords` the
 *     moment the user runs BLOCK (createBlockDefinitionInDocument registers the
 *     user block). It was mis-detected as imported → it shipped the
 *     non-conformant DXF (non-hex handles, no skeleton).
 * The marker (+ raw-section secondary signal) removes the ambiguity.
 */
function isFromScratchDocument(document: DrawingDocument): boolean {
  // No importer marker AND no imported raw SECTION source ⇒ a from-scratch
  // (File → New + draw) document, even if it has since gained user blocks /
  // block records / raw entity groups (the round-14 rawEntityGroups guard).
  return !isImportedSourceDocument(document);
}

/**
 * The result of reconciling a from-scratch document's paper-space tabs against
 * the skeleton's single `*Paper_Space` BLOCK_RECORD + `Layout1` LAYOUT (ROOT C).
 */
interface PaperReconciliation {
  /** Final `blockRecords` map (skeleton's `*Model_Space`/`*Paper_Space` + one
   *  fresh `*Paper_Space<N>` record per ADDITIONAL paper tab). */
  blockRecords: Record<string, BlockRecord>;
  /** The minted `*Paper_Space<N>` BLOCK definitions (keyed by name) so each
   *  extra paper BLOCK_RECORD has a matching BLOCK in the BLOCKS section (Codex
   *  round-4 P2a — a dangling BLOCK_RECORD with no BLOCK is rejected by AutoCAD). */
  paperBlocks: Record<string, BlockDefinition>;
  /** Final `layouts` map: skeleton's `Model` + one minted LAYOUT per paper tab
   *  (keyed by space name), with user `document.layouts` entries winning by
   *  name. The phantom skeleton `Layout1` is NEVER carried. */
  layouts: Record<string, Layout>;
  /** Per space.id → owning BLOCK_RECORD handle (DXF group 330), for the entity
   *  owner-handle wiring pass. Model spaces map to `*Model_Space`; each paper
   *  tab maps to ITS OWN `*Paper_Space`/`*Paper_Space<N>` record. */
  ownerBySpaceId: Map<string, string | undefined>;
  /** The ACAD_LAYOUT dictionary handle (skeleton group 5 = "D"), so the caller
   *  can patch the dictionary's child-entry list in rawObjectGroups. */
  layoutDictHandle: string | undefined;
  /** name → LAYOUT handle for EVERY layout that must appear in the ACAD_LAYOUT
   *  dictionary (Model + each paper tab), captured PRE-remint.
   *  FIX 3 (P1): the caller no longer uses this snapshot for the dictionary
   *  rewrite — it recomputes the `3/350` entry list from the POST-remint
   *  `finalLayouts` (after `remintRecordOwnHandles`), so a layout reminted from a
   *  blank/non-hex handle stays in sync with its emitted AcDbLayout object. Kept
   *  for reference/diagnostics (and asserted equal-set, modulo reminted handles,
   *  in tests). */
  layoutDictEntries: { name: string; handle: string }[];
}

/**
 * ROOT C — reconcile a from-scratch document's `spaces` (Model + N paper tabs)
 * against the skeleton's single paper BLOCK_RECORD + phantom `Layout1` LAYOUT.
 *
 * A File → New document seeds paper tabs as `kind:"layout"` SPACES (e.g.
 * "Layout 1" / "Layout 2") with `document.layouts === {}`. The naive
 * `{...skeleton.layouts, ...document.layouts}` merge adopted the skeleton's
 * phantom `Layout1` AND collapsed every paper tab onto the single skeleton
 * `*Paper_Space`. This reconciliation instead:
 *   (1) DROPS the skeleton's `Layout1` (keeps only its `Model` LAYOUT);
 *   (2) mints a LAYOUT object per paper SPACE that lacks a `document.layouts`
 *       entry (reusing {@link buildDefaultLayout});
 *   (3) mints a fresh `*Paper_Space<N>` BLOCK_RECORD per ADDITIONAL paper tab
 *       (the FIRST paper tab reuses the skeleton's `*Paper_Space`);
 *   (4) wires LAYOUT.ownerBlockRecordHandle (330) ↔ BLOCK_RECORD.layoutHandle
 *       (340) and records each paper space's own owner handle for the entity
 *       routing pass;
 *   (5) lets user `document.layouts` entries win by name.
 */
function reconcilePaperSpaces(
  document: DrawingDocument,
  skeleton: DrawingDocument,
  mintHandle: () => string,
): PaperReconciliation {
  const blockRecords = structuredClone(skeleton.blockRecords ?? {});
  // Codex round-4 P2a — the minted `*Paper_Space<N>` BLOCK definitions, keyed by
  // name. Each extra paper BLOCK_RECORD below MUST have a matching BLOCK here so
  // the BLOCKS section is not missing it (else the record is dangling).
  const paperBlocks: Record<string, BlockDefinition> = {};
  // (1) Carry ONLY the skeleton's `Model` LAYOUT — drop the phantom `Layout1`.
  const layouts: Record<string, Layout> = {};
  const skeletonModel = skeleton.layouts?.[MODEL_LAYOUT_NAME];
  if (skeletonModel) layouts[MODEL_LAYOUT_NAME] = structuredClone(skeletonModel);
  // The ACAD_LAYOUT dictionary handle that owns every LAYOUT object — read off
  // the skeleton's `Model` LAYOUT (its group-330 dictionary owner). Each minted
  // paper LAYOUT is owned by the SAME dictionary.
  const layoutDictHandle = skeletonModel?.dictionaryOwnerHandle;

  const ownerBySpaceId = new Map<string, string | undefined>();
  const modelOwner = blockRecords[MODEL_SPACE_BLOCK_RECORD]?.handle;
  const skeletonPaperRecord = blockRecords[PAPER_SPACE_BLOCK_RECORD];

  let paperTabIndex = 0;
  // Suffix counter for ADDITIONAL paper blocks, matching ezdxf's canonical
  // sequence: the first extra paper block is `*Paper_Space0`, the next
  // `*Paper_Space1`, … (confirmed against a native ezdxf multi-paperspace
  // round-trip). Independent of `paperTabIndex` so it stays correct even on the
  // defensive path where the skeleton lacks a `*Paper_Space` record and the
  // FIRST tab must itself mint the suffix-0 block.
  let additionalPaperIndex = 0;
  let tabOrder = 1; // Model occupies tab 0.

  // Mint a fresh `*Paper_Space<N>` BLOCK_RECORD + its matching BLOCK def (so the
  // record is never dangling — AutoCAD/ezdxf reject a BLOCK_RECORD with no BLOCK).
  // Shared by the matched-space loop AND the leftover-layouts loop so EVERY
  // emitted paper LAYOUT has a real owning BLOCK_RECORD (a LAYOUT whose group-330
  // owner does not resolve = ezdxf `DXFStructureError: required BLOCK_RECORD for
  // layout '<name>' does not exist`). Returns the new record (its `.handle` is the
  // LAYOUT's `ownerBlockRecordHandle`). Naming follows ezdxf's canonical suffix
  // sequence (`*Paper_Space0`, `*Paper_Space1`, …).
  const mintAdditionalPaperRecord = (): BlockRecord => {
    const recordName = `${PAPER_SPACE_BLOCK_RECORD}${additionalPaperIndex}`;
    additionalPaperIndex += 1;
    const record: BlockRecord = {
      name: recordName,
      handle: mintHandle(),
      insertionUnits: 0,
      explodable: true,
      scalable: "non-uniform",
      isAnnotative: false,
      annotativeDecompositions: {},
      description: "",
    };
    blockRecords[recordName] = record;
    const skeletonPaperBlock = skeleton.blocks?.[PAPER_SPACE_BLOCK_RECORD];
    const paperBlock: BlockDefinition = skeletonPaperBlock
      ? {
          ...structuredClone(skeletonPaperBlock),
          name: recordName,
          handle: mintHandle(),
          ownerHandle: record.handle,
          // T9b — the clone must NOT inherit the skeleton `*Paper_Space` ENDBLK
          // handle (a duplicate group-5); mint its own like the BLOCK handle.
          endblkHandle: mintHandle(),
          entities: [],
        }
      : {
          name: recordName,
          handle: mintHandle(),
          ownerHandle: record.handle,
          layer: "0",
          flags: 0,
          basePoint: { x: 0, y: 0, z: 0 },
          xrefPathName: "",
          description: "",
          entities: [],
          dynamicBlock: false,
          xrefStatus: "resolved",
        };
    paperBlocks[recordName] = paperBlock;
    return record;
  };

  for (const space of document.spaces) {
    if (space.kind === "model") {
      ownerBySpaceId.set(space.id, modelOwner);
      continue;
    }
    if (space.kind !== "layout") {
      // block-test / block-edit sandboxes never reach the emitted DXF.
      ownerBySpaceId.set(space.id, undefined);
      continue;
    }

    // (3) Block record: first paper tab reuses the skeleton's `*Paper_Space`;
    // each ADDITIONAL tab gets a fresh `*Paper_Space<N>` with a new hex handle.
    // ezdxf's canonical suffix sequence for the extra paper blocks starts at `0`
    // for the SECOND paper tab (`*Paper_Space0`, `*Paper_Space1`, …) — confirmed
    // against a native ezdxf multi-paperspace round-trip. Naming the 2nd tab
    // `*Paper_Space1` breaks the sequence ezdxf's loader pairs paperspace
    // ENTITIES to its block by, so the tab's entity is re-homed onto
    // `*Paper_Space` (the entity never lands in its own tab even though its raw
    // 330 owner is correct). `additionalPaperIndex` (suffix counter, starts at 0)
    // keeps the names matching ezdxf.
    // (3) Block record: first paper tab reuses the skeleton's `*Paper_Space`;
    // each ADDITIONAL tab gets a fresh `*Paper_Space<N>` (Codex round-4 P2a).
    let record: BlockRecord | undefined;
    if (paperTabIndex === 0 && skeletonPaperRecord) {
      record = blockRecords[PAPER_SPACE_BLOCK_RECORD];
    } else {
      record = mintAdditionalPaperRecord();
    }
    paperTabIndex += 1;
    const ownerHandle = record?.handle;
    ownerBySpaceId.set(space.id, ownerHandle);

    // (2) LAYOUT object keyed by the SPACE name. A user `document.layouts` entry
    // (by name) wins; otherwise mint a default-page-setup LAYOUT.
    const userLayout = document.layouts?.[space.name];
    const layout: Layout = userLayout
      ? structuredClone(userLayout)
      : buildDefaultLayout(space.name, tabOrder);
    // (4) Wire the LAYOUT ↔ BLOCK_RECORD handle pair (fresh hex). The LAYOUT
    // needs its own handle (group 5) so the OBJECTS record + the BLOCK_RECORD
    // 340 back-reference resolve under strict readfile.
    if (!isHexHandle(layout.handle)) layout.handle = mintHandle();
    layout.ownerBlockRecordHandle = ownerHandle ?? layout.ownerBlockRecordHandle;
    // Own the minted LAYOUT by the skeleton's ACAD_LAYOUT dictionary so the
    // writer emits the AcDbLayout 330 (BLOCK_RECORD back-ref) — the pointer
    // ezdxf pairs LAYOUT↔BLOCK_RECORD by (ROOT C).
    if (layoutDictHandle) layout.dictionaryOwnerHandle = layoutDictHandle;
    if (record) record.layoutHandle = layout.handle;
    layouts[space.name] = layout;
    tabOrder += 1;
  }

  // Codex P2 #3 — MODEL-ONLY doc (no paper SPACE consumed the skeleton's
  // `*Paper_Space`, e.g. a WBLOCK block/object export): KEEP the skeleton's
  // `Layout1` ↔ `*Paper_Space` pairing intact. Dropping it (as the user-paper
  // path does) would leave `*Paper_Space.layoutHandle` (340 = skeleton 1E)
  // pointing at a MISSING LAYOUT (dangling BLOCK_RECORD→LAYOUT) AND remove the
  // last paper layout AutoCAD requires. The skeleton's `*Paper_Space` record
  // still carries its original `layoutHandle` (untouched by the loop), so we
  // re-adopt the matching `Layout1` LAYOUT object verbatim.
  if (paperTabIndex === 0) {
    const skeletonPaperLayout =
      skeleton.layouts?.[SKELETON_PAPER_LAYOUT_NAME];
    if (skeletonPaperLayout) {
      layouts[SKELETON_PAPER_LAYOUT_NAME] =
        structuredClone(skeletonPaperLayout);
    }
  }

  // (5) Any remaining user `document.layouts` entries not matched to a paper
  // SPACE above still win by name (e.g. a layout with no live space yet).
  for (const [name, layout] of Object.entries(document.layouts ?? {})) {
    if (name === MODEL_LAYOUT_NAME) continue;
    if (name === SKELETON_PAPER_LAYOUT_NAME && SKELETON_PAPER_LAYOUT_NAME in layouts) {
      // The model-only guard above re-adopted the skeleton `Layout1` — do not
      // let a same-named user entry clobber the skeleton-paired LAYOUT handle.
      continue;
    }
    if (!(name in layouts)) {
      const cloned = structuredClone(layout);
      // FIX 3 — a leftover layout (no matching live SPACE) must be wired EXACTLY
      // like the matched path, or ezdxf rejects the file with
      // `DXFStructureError: required BLOCK_RECORD for layout '<name>' does not
      // exist`. The matched loop (above) gives every paper LAYOUT (a) a fresh hex
      // own-handle (group 5), (b) an owning `*Paper_Space<N>` BLOCK_RECORD wired
      // both ways (LAYOUT.ownerBlockRecordHandle 330 ⇄ record.layoutHandle 340),
      // and (c) ownership by the ACAD_LAYOUT dictionary (330). Replicate all three
      // here so the leftover LAYOUT is a structurally complete, non-dangling tab.
      const record = mintAdditionalPaperRecord();
      if (!isHexHandle(cloned.handle)) cloned.handle = mintHandle();
      // `mintAdditionalPaperRecord` always mints a hex `record.handle`; the `??`
      // mirrors the matched path's narrowing (BlockRecord.handle is `string |
      // undefined` in the schema) and never falls through in practice.
      cloned.ownerBlockRecordHandle =
        record.handle ?? cloned.ownerBlockRecordHandle;
      if (layoutDictHandle) cloned.dictionaryOwnerHandle = layoutDictHandle;
      record.layoutHandle = cloned.handle;
      // No live SPACE owns this leftover tab, so no entity routing entry is
      // needed in `ownerBySpaceId` (it keys by space.id; there is none here).
      layouts[name] = cloned;
    }
  }

  // The exact set of (name → handle) entries the ACAD_LAYOUT dictionary must
  // list (Model + every paper tab) so the caller can rewrite the dictionary's
  // child list — dropping the phantom `Layout1` and registering each minted tab.
  const layoutDictEntries = Object.values(layouts).map((l) => ({
    name: l.name,
    handle: l.handle,
  }));

  return {
    blockRecords,
    paperBlocks,
    layouts,
    ownerBySpaceId,
    layoutDictHandle,
    layoutDictEntries,
  };
}

/**
 * ROOT C — rewrite the ACAD_LAYOUT DICTIONARY's child-entry list inside the
 * verbatim `rawObjectGroups` so it lists EXACTLY the reconciled layouts (Model +
 * every paper tab) under their fresh handles. The skeleton's dictionary lists
 * `Model→1A` + the phantom `Layout1→1E`; without this rewrite the phantom entry
 * dangles (its `1E` LAYOUT was dropped) and the minted paper tabs are missing
 * from the dictionary (orphan LAYOUT objects → ezdxf cannot enumerate the tabs).
 *
 * A dictionary entry is a `3 <name>` / `350|360 <handle>` pair after the
 * `100 AcDbDictionary` marker. We locate the dictionary whose group-5 handle is
 * `dictHandle`, strip its existing `3/350|360` child pairs, and splice in one
 * `3 <name>` / `350 <handle>` pair per reconciled entry (keeping every other
 * group — 0/5/330/280/281/100 — verbatim). Returns a NEW group array (pure).
 */
export function patchLayoutDictionaryEntries(
  groups: { code: number; value: string }[],
  dictHandle: string,
  entries: { name: string; handle: string }[],
): { code: number; value: string }[] {
  // Locate the dictionary record boundaries (its `0/DICTIONARY` start → next 0).
  const recordStarts: number[] = [];
  for (let i = 0; i < groups.length; i += 1) {
    if (groups[i].code === 0) recordStarts.push(i);
  }
  for (let r = 0; r < recordStarts.length; r += 1) {
    const start = recordStarts[r];
    const end = r + 1 < recordStarts.length ? recordStarts[r + 1] : groups.length;
    const typeValue = groups[start].value;
    if (typeValue !== "DICTIONARY" && typeValue !== "ACDBDICTIONARYWDFLT") {
      continue;
    }
    // Match by the record's own handle (group 5).
    let recHandle: string | undefined;
    for (let i = start + 1; i < end; i += 1) {
      if (groups[i].code === 5) {
        recHandle = groups[i].value.trim();
        break;
      }
    }
    if (recHandle?.toLowerCase() !== dictHandle.toLowerCase()) continue;

    // Rebuild this record: keep every group EXCEPT the `3 <name>` / `350|360
    // <handle>` child pairs; re-insert the fresh child list right after the
    // `100 AcDbDictionary` marker (or, defensively, at the record end).
    const head: { code: number; value: string }[] = [];
    let insertAfter = -1;
    for (let i = start; i < end; i += 1) {
      const g = groups[i];
      if (g.code === 3) continue; // drop child name
      if (g.code === 350 || g.code === 360) continue; // drop child handle
      head.push(g);
      if (g.code === 100 && g.value.trim() === "AcDbDictionary") {
        insertAfter = head.length;
      }
    }
    const childGroups: { code: number; value: string }[] = [];
    for (const e of entries) {
      childGroups.push({ code: 3, value: e.name });
      childGroups.push({ code: 350, value: e.handle });
    }
    const at = insertAfter >= 0 ? insertAfter : head.length;
    const rebuilt = [...head.slice(0, at), ...childGroups, ...head.slice(at)];
    return [...groups.slice(0, start), ...rebuilt, ...groups.slice(end)];
  }
  // Dictionary not found — return unchanged (defensive; skeleton always has it).
  return groups;
}

/**
 * FIX-202 — sweep DANGLING `3/name 350|360/handle` children out of every
 * DICTIONARY / ACDBDICTIONARYWDFLT record in a raw OBJECTS group stream whose
 * target handle is NOT in `emittedHandles` (the set of own-handles that the
 * export actually emits).
 *
 * Root cause: the skeleton template's `ACAD_MLEADERSTYLE` dictionary carries a
 * `Standard → 350/<h>` child pointing at the template's `Standard` MLEADERSTYLE
 * object. The parser PROMOTES that MLEADERSTYLE OUT of `rawObjectGroups` into the
 * typed `styleTables.multileaderStyles` WITHOUT its own handle, and the
 * from-scratch export re-emits NO MLEADERSTYLE object (`multileaderStyles: []`),
 * so the dictionary's `350/<h>` child dangles → ezdxf `audit()` SILENTLY removes
 * it ("Removed invalid key Standard in DICTIONARY(#…)") → AutoCAD recovery.
 * Pre-emptively dropping the unresolved child makes the emitted DICTIONARY
 * self-consistent (no dangling reference), which is exactly the conformant
 * outcome ezdxf would repair to.
 *
 * The hard-owned-by-default child of an ACDBDICTIONARYWDFLT (group 340) is the
 * dictionary's DEFAULT entry, not a named `3/350` child, so it is never a sweep
 * candidate (only `3` + `350|360` PAIRS are). Pure — returns a new array.
 */
export function dropDanglingDictionaryKeys(
  groups: { code: number; value: string }[],
  emittedHandles: ReadonlySet<string>,
): { code: number; value: string }[] {
  const recordStarts: number[] = [];
  for (let i = 0; i < groups.length; i += 1) {
    if (groups[i].code === 0) recordStarts.push(i);
  }
  const out: { code: number; value: string }[] = [];
  for (let r = 0; r < recordStarts.length; r += 1) {
    const start = recordStarts[r];
    const end =
      r + 1 < recordStarts.length ? recordStarts[r + 1] : groups.length;
    const typeValue = groups[start].value;
    if (typeValue !== "DICTIONARY" && typeValue !== "ACDBDICTIONARYWDFLT") {
      for (let i = start; i < end; i += 1) out.push(groups[i]);
      continue;
    }
    // Walk the record; when a `3/name` is immediately followed by a `350|360`
    // child handle that is NOT emitted, DROP both groups. Any other group
    // (including a lone `3` with no following handle, or a `340` default child)
    // passes through unchanged.
    for (let i = start; i < end; i += 1) {
      const g = groups[i];
      const next = i + 1 < end ? groups[i + 1] : undefined;
      if (
        g.code === 3 &&
        next !== undefined &&
        (next.code === 350 || next.code === 360) &&
        !emittedHandles.has(next.value.trim().toLowerCase())
      ) {
        i += 1; // skip the dangling child handle too
        continue;
      }
      out.push(g);
    }
  }
  return out;
}

/**
 * FINDING 3 — the DXF group codes that carry a record's OWN group-5 handle (the
 * thing the writer emits as `5 <handle>` / `105 <handle>` and that a DICTIONARY
 * `350|360` child resolves AGAINST). 1005 is DELIBERATELY EXCLUDED — it is an
 * XData handle REFERENCE, never an own handle, so counting it as "emitted" would
 * mark a dangling dict child as resolved. Distinct from `OWN_HANDLE_GROUP_CODES`
 * (which includes 1005 for the entity-minter seed, where over-collection is
 * SAFE; here over-collection LEAVES a dangling key in → ezdxf re-repairs it).
 */
const EMITTED_OWN_HANDLE_CODES = new Set<number>([5, 105]);

/**
 * FINDING 3 — collect the FIRST `{code:5|105}` (own group-5 handle) of EACH raw
 * record in a raw group stream (`rawObjectGroups` / `rawTableGroups` / a
 * passthrough section). A record opens at each `{code:0}` marker; only the FIRST
 * 5/105 inside it is the record's own handle — a later 5 (e.g. a nested vendor
 * group) or any reference code (330/340/350/360/1005) is NOT an own handle and
 * MUST NOT count as "emitted" for the dangling-key census.
 */
export function collectRawOwnHandles(
  groups: { code: number; value: string }[] | undefined,
  out: Set<string>,
): void {
  if (!groups) return;
  let ownSeen = false;
  for (const g of groups) {
    if (g.code === 0) {
      ownSeen = false; // a new record begins
      continue;
    }
    if (!ownSeen && EMITTED_OWN_HANDLE_CODES.has(g.code) && g.value.length > 0) {
      ownSeen = true; // only the first 5/105 per record is the own handle
      out.add(g.value.trim().toLowerCase());
    }
  }
}

/**
 * FINDING 3 — deep-walk a TYPED object graph collecting only OWN group-5 handles
 * (the values the export emits as `5 <handle>`), NOT reference handles:
 *   - the bare `handle` key (a record's own handle: block / blockRecord / layout
 *     / layer, plus a nested ATTRIB/ATTDEF child's own group-5),
 *   - an entity's `id` (the top-level entity own group-5 — `toDxfEntity` stamps
 *     `common.handle = entity.id`), recognised by the sibling `type` discriminant
 *     so a non-entity `id` (a `spaceId`, a visibility-state id) is never harvested.
 * It DELIBERATELY SKIPS every `*Handle` reference key (`ownerHandle`,
 * `layoutHandle`, `clippingBoundaryHandle`, …), every `*Handles` array, and the
 * `{code,handle}` (viewport `layerOverrideHandles` entry, 335/343/344/91 →
 * LAYER / override OBJECT) / `{code:1005,value}` reference carriers — discriminated
 * by a numeric `code` SIBLING and returned early before the key-walk — including
 * one of THOSE was the defect: a dangling dict child whose target appears merely as a
 * REFERENCE elsewhere was wrongly treated as resolved → the dangling key was
 * LEFT IN → ezdxf re-repaired it (FIX 202 returns). Contrast `collectHandlesDeep`
 * (annotativeExport), which harvests EVERY reference for the over-collection-safe
 * MINTER seed — the wrong set for this census.
 */
export function collectTypedOwnHandles(node: unknown, out: Set<string>): void {
  if (node == null) return;
  if (Array.isArray(node)) {
    for (const item of node) collectTypedOwnHandles(item, out);
    return;
  }
  if (typeof node !== "object") return;
  const record = node as Record<string, unknown>;
  // FINDING 1 (T9 round-2 edge) — a `{code:number, handle:string}` REFERENCE
  // carrier (the viewport `layerOverrideHandles` entry: 335/343/344/91 → LAYER /
  // override OBJECTS) is NOT an own group-5 handle — the writer emits its `handle`
  // as a 335/343/344/91 REFERENCE, never as `5 <handle>`. Its discriminant is the
  // numeric `code` SIBLING next to the `handle` (the SAME predicate
  // `rewriteArrayEntries` / `remapEntityHandleReferences` use to tell it apart
  // from an ATTRIB attribute's own `handle`, which carries NO numeric `code`).
  // Without this guard the generic key-walk below mistakes the carrier's `handle`
  // for an own handle → the dangling-key census OVER-COUNTS a reference as
  // "resolved" → a stray dict child whose target is merely such a reference is
  // wrongly KEPT (the very class round-2's own-handle census set out to drop).
  // Return early — these carriers nest no own-handle field.
  if (typeof record.code === "number" && typeof record.handle === "string") {
    return;
  }
  // A RAW entity-group record (`{entityType, groups}`) nested inside a typed
  // structure (a `block.rawEntityGroups` child whose own 5/105 handle the export
  // emits verbatim). Harvest its OWN handle the same first-5/105-per-record way as
  // the top-level raw streams — recursing the generic walk would only see the
  // `{code,value}` leaves, which carry no `handle`/`id` key, so the own handle
  // would be MISSED (a dict child targeting a raw block child would dangle).
  if (typeof record.entityType === "string" && Array.isArray(record.groups)) {
    collectRawOwnHandles(
      record.groups as { code: number; value: string }[],
      out,
    );
    return;
  }
  // An entity's own group-5 is its `id` (the writer stamps `common.handle =
  // entity.id`). Recognise an entity by its discriminant `type` field so a
  // non-entity `id` (space id, document id) is never mistaken for a handle.
  if (typeof record.type === "string" && typeof record.id === "string" && record.id.length > 0) {
    out.add(record.id.toLowerCase());
  }
  for (const [key, value] of Object.entries(record)) {
    // The bare `handle` key is an own group-5 key ONLY on a record / nested
    // ATTRIB-ATTDEF shape (no numeric `code` sibling — the carrier shape was
    // already returned above). Every `*Handle` / `*Handles` key is a REFERENCE —
    // skipped (the walk only matches the exact `handle` key, never a suffix) —
    // EXCEPT the T9b OWN keys (`OWN_HANDLE_KEYS`): a BLOCK's `endblkHandle` and
    // an INSERT's `seqendHandle` are emitted as `5 <h>` on the reconstructed
    // ENDBLK / SEQEND (classed `'own'` in HANDLE_FIELD_CLASS).
    if (
      OWN_HANDLE_KEYS.has(key) &&
      typeof value === "string" &&
      value.length > 0
    ) {
      out.add(value.toLowerCase());
      continue;
    }
    if (value != null && typeof value === "object") {
      collectTypedOwnHandles(value, out);
    }
  }
}

/**
 * Collect every handle the skeleton already uses (lowercased) so the entity
 * re-minter never aliases a structural record. Walks the verbatim raw group
 * streams (group 5 / 1005 / handle-shaped values are all encoded as `code:5`
 * leaves there) plus the typed block-record / layout handles.
 */
function collectSkeletonHandles(skeleton: DrawingDocument): Set<string> {
  const used = new Set<string>();
  const harvest = (groups: { code: number; value: string }[] | undefined) => {
    for (const g of groups ?? []) {
      // DXF handle-bearing group codes that may appear in the raw streams —
      // SHARED with the export census via `OWN_HANDLE_GROUP_CODES` (5 general
      // own-handle, 105 DIMSTYLE own-handle, 1005 xdata ref) so the two sites
      // can never diverge on which codes carry a handle.
      if (OWN_HANDLE_GROUP_CODES.has(g.code) && g.value.length > 0) {
        used.add(g.value.toLowerCase());
      }
    }
  };
  harvest(skeleton.rawTableGroups);
  harvest(skeleton.rawObjectGroups);
  for (const section of skeleton.rawPassthroughSections ?? []) {
    harvest(section.groups);
  }
  for (const record of Object.values(skeleton.blockRecords ?? {})) {
    if (record.handle) used.add(record.handle.toLowerCase());
  }
  for (const layout of Object.values(skeleton.layouts ?? {})) {
    if (layout.handle) used.add(layout.handle.toLowerCase());
    if (layout.ownerBlockRecordHandle) {
      used.add(layout.ownerBlockRecordHandle.toLowerCase());
    }
  }
  return used;
}

/**
 * Patch (or insert) the HEADER `$HANDSEED` value so it sits strictly above
 * `maxHandle`. AutoCAD requires `$HANDSEED` to exceed every handle in use so a
 * subsequent edit mints non-colliding handles. The skeleton ships a low seed
 * (e.g. 0x32); after re-minting entity handles into the 0x100000+ range the seed
 * must be bumped. Returns a new section array (pure).
 */
function patchHandSeed(
  sections: NonNullable<DrawingDocument["rawPassthroughSections"]>,
  maxHandle: number,
): NonNullable<DrawingDocument["rawPassthroughSections"]> {
  const seedHex = (maxHandle + 1).toString(16).toUpperCase();
  return sections.map((section) => {
    if (section.name !== "HEADER") return section;
    const groups = section.groups.map((g) => ({ ...g }));
    // $HANDSEED is encoded as a `9 $HANDSEED` name followed by a `5 <hex>`
    // value group. Find the name marker and overwrite the following value.
    for (let i = 0; i < groups.length - 1; i += 1) {
      if (groups[i].code === 9 && groups[i].value === "$HANDSEED") {
        // The value group is the next group (DXF group code 5 for $HANDSEED).
        if (groups[i + 1]) groups[i + 1] = { code: 5, value: seedHex };
        return { ...section, groups };
      }
    }
    return { ...section, groups };
  });
}

/**
 * Back-fill the conformant DXF skeleton onto a FROM-SCRATCH document so its
 * export is AutoCAD/ezdxf-readable. Imported documents are returned UNCHANGED
 * (their byte-faithful carry-forward must not be disturbed). The returned value
 * is always a fresh object graph for the from-scratch path — the input is never
 * mutated.
 *
 * `carriedObjectRecords` are OBJECTS-section records an ENTITY of this document
 * hard-points at and that therefore have to travel with it — an `ACAD_TABLE`'s
 * group-342 `TABLESTYLE` is the first of them (Autodesk ObjectARX *Deep Clone
 * Basics*: a wblock clone clones hard owners and hard pointers). They are passed
 * BESIDE the document rather than on `rawObjectGroups` because that field is one
 * of the by-construction IMPORTED-SOURCE signals {@link isImportedSourceDocument}
 * reads — a from-scratch document that set it would be passed through here and
 * lose the entire skeleton. See `dxfBridge.carriedObjectRecords.ts` for the merge
 * contract; an imported document ignores them (it already carries its own
 * OBJECTS stream verbatim).
 */
export function ensureConformantSkeleton(
  document: DrawingDocument,
  carriedObjectRecords: readonly CarriedObjectRecord[] = [],
): DrawingDocument {
  if (!isFromScratchDocument(document)) return document;

  const skeleton = getSkeletonDocument();
  const skeletonHandles = collectSkeletonHandles(skeleton);
  // Codex round-2 P2 — seed the minter from the DOCUMENT'S FULL existing handle
  // population, NOT just the blank skeleton's low handles. A from-scratch wrapper
  // doc can CARRY copied source handles in the 0x100000+ mint range: a WBLOCK
  // `block`/`objects` export from an IMPORTED drawing deep-clones the source's
  // `blocks` / `blockRecords` / style tables / raw block payloads (with their
  // ORIGINAL handles) into a fresh, provenance-less (⇒ from-scratch ⇒ reminted)
  // wrapper. Seeding ONLY from the blank skeleton left the minter blind to those
  // copied handles, so the first reminted entity/paper-record handle could ALIAS
  // a copied one (e.g. `100001`) → DUPLICATE group-5 handles → strict ezdxf
  // rejects the file. `collectHandlesDeep` walks every `handle`/`*Handle` field +
  // verbatim group-5/1005/105 leaf across the copied blocks/blockRecords/style
  // tables/raw groups so no minted handle collides with a copied source handle.
  const seedHandles = new Set(skeletonHandles);
  // BUG 3 (round-11 P2) — seed the SHARED minter with the skeleton's own BLOCK
  // header handles (`*Model_Space` = "18", `*Paper_Space` = "1C"). They live ONLY
  // in the typed `skeleton.blocks` map; `collectSkeletonHandles` walks the raw
  // group streams + typed blockRecords + layouts but NEVER `skeleton.blocks`, so
  // the BLOCK header handles were absent from the minter's used-set. Without them
  // the minter's `has`/`reserve` collision check (BUG 3) would treat a from-scratch
  // nested own handle of "18" as FREE and keep it verbatim → DUPLICATE group-5 once
  // the skeleton BLOCK emits "18" (and the typed ATTDEFs are now emitted too).
  // `collectHandlesDeep` harvests `block.handle` (+ any nested handle) so the minter
  // KNOWS "18"/"1C" are taken → an already-hex "18" is reminted, not kept.
  collectHandlesDeep(skeleton.blocks, seedHandles);
  collectHandlesDeep(skeleton.blockRecords, seedHandles);
  collectHandlesDeep(document.blocks, seedHandles);
  collectHandlesDeep(document.blockRecords, seedHandles);
  collectHandlesDeep(document.styleTables, seedHandles);
  collectHandlesDeep(document.layouts, seedHandles);
  collectHandlesDeep(document.rawTableGroups, seedHandles);
  collectHandlesDeep(document.rawObjectGroups, seedHandles);
  collectHandlesDeep(document.rawPassthroughSections, seedHandles);
  // Defect #7 (seed completeness) — also walk `document.spaces`. A copied
  // imported handle in the 0x100000+ range can live on a NESTED ENTITY field
  // (e.g. an INSERT `attributes[].handle`, a HATCH `sourceBoundaryHandles`, an
  // MLEADER `blockRecordHandle`, a viewport `clippingBoundaryHandle`) that the
  // blocks/blockRecords/styleTables/raw walks never reach — without seeding from
  // spaces the entity re-minter could mint a handle that ALIASES that copied one
  // → DUPLICATE group-5. Seeding here is sound because the entity re-minter
  // OVERWRITES every `entity.id` anyway (so seeding the old ids costs nothing).
  collectHandlesDeep(document.spaces, seedHandles);
  // FIX C (round-9 #3 / SG-4) — also seed `document.rawEntityGroups`. It is a
  // TOP-LEVEL field (the ENTITIES-section model/paper-space analogue of
  // `block.rawEntityGroups`) that the seed block above never walked, so a copied
  // imported handle living on a raw ENTITIES-section child (a `{code:5}` own
  // handle, a `{code:1005}` xdata ref) was invisible to the minter → a minted
  // entity/record handle could ALIAS it (duplicate group-5). `collectHandlesDeep`
  // harvests the `{code:5|105|1005}` leaves via its `OWN_HANDLE_GROUP_CODES` walk.
  // (`block.rawEntityGroups` is already reached via `collectHandlesDeep(document.blocks)`.)
  collectHandlesDeep(document.rawEntityGroups, seedHandles);
  // FIX-110 — seed the LAYER / STYLE / DIMSTYLE / BLOCK_RECORD table-head
  // handles the from-scratch doc ADOPTS from the skeleton template (handles
  // `1/5/4/9` etc.). These are LOW-range and the entity minter bases at
  // 0x100000+, so a collision is unlikely — but seeding them is correct and
  // defensive: the head handle is a real own-handle in the emitted DXF, so the
  // structural reminter / entity minter must never alias it. `collectHandlesDeep`
  // harvests the `{code:5}` head handle from each captured head group array.
  collectHandlesDeep(skeleton.symbolTableHeaders, seedHandles);
  // FIX-110 — likewise seed the LTYPE table-head handle the from-scratch doc
  // adopts from the skeleton template (handle `2`). Same low-range / defensive
  // reasoning as the four symbol-table heads above.
  collectHandlesDeep(skeleton.linetypeTableHeader, seedHandles);
  // A SINGLE minter shared by the paper-record reconciliation AND the entity
  // re-minter so a minted `*Paper_Space<N>` handle never collides with an entity
  // handle (both draw from the 0x100000+ range, collision-checked against the
  // skeleton's structural handles + the document's copied source handles).
  const mintHandle = makeHandleMinter(seedHandles, ENTITY_HANDLE_BASE);

  // Adopt the skeleton's structural records (deep-cloned so the cached skeleton
  // and the live input are both untouched). MERGE the user's own blocks /
  // block records ON TOP of the skeleton's `*Model_Space` / `*Paper_Space`
  // structural records so a from-scratch doc that ran BLOCK keeps its user
  // block definition + record (the skeleton supplies ONLY the two anonymous
  // space blocks; the user's named blocks must survive).
  //
  // Defect #6 (INSERT clobber) — an INSERT-folded imported wrapper can carry the
  // reserved `*Model_Space` / `*Paper_Space` blocks + records with the IMPORTED
  // SOURCE handle. Spreading them verbatim OVER the skeleton records would
  // replace the skeleton record (whose handle `ownerBySpaceId` was derived from)
  // with a different imported handle AFTER the owner wiring → every reminted
  // model/paper entity's `ownerHandle` (330) points at the SKELETON handle while
  // the emitted BLOCK_RECORD carries the IMPORTED handle = a DANGLING owner. We
  // therefore merge ONLY USER-NAMED records — the skeleton's reserved space
  // blocks/records are SKELETON-OWNED and never overwritten.
  // ROOT C — reconcile paper tabs: drop the phantom `Layout1`, mint a LAYOUT +
  // (per ADDITIONAL tab) a `*Paper_Space<N>` BLOCK_RECORD + its matching BLOCK
  // def (Codex round-4 P2a), and record each space's own owner BLOCK_RECORD
  // handle. Runs BEFORE the entity re-mint so the shared minter assigns disjoint
  // handles. The user's BLOCK_RECORDs (from BLOCK) are merged over the skeleton's
  // records — but NOT the reserved space records (defect #6 above). Hoisted ABOVE
  // the `blocks` build so the minted `*Paper_Space<N>` BLOCK defs spread in (a
  // BLOCK_RECORD with no BLOCK is a dangling record AutoCAD rejects).
  const reconciliation = reconcilePaperSpaces(document, skeleton, mintHandle);
  const blocks = {
    ...structuredClone(skeleton.blocks ?? {}),
    ...reconciliation.paperBlocks, // minted *Paper_Space<N> BLOCK defs (P2a)
    ...userNamedRecords(structuredClone(document.blocks ?? {})),
  };
  // T9b — materialise the anonymous `*D<n>` block of every DIMENSION in the
  // TYPED model before any handle pass, so its BLOCK / ENDBLK / BLOCK_RECORD
  // handles + owners come from the same record machinery as a user block (the
  // writer's fallback stub was handle-less and record-less → ezdxf silently
  // minted all three). The per-entity name is stamped on the clones in PASS 2.
  const anonymousDimBlocks = assignAnonymousDimensionBlocks(document, blocks);
  Object.assign(blocks, anonymousDimBlocks.blocks);
  const rawTableGroups = structuredClone(skeleton.rawTableGroups ?? []);
  let rawObjectGroups = structuredClone(skeleton.rawObjectGroups ?? []);

  // Merge the OBJECTS records the caller carries on behalf of a hard-pointing
  // entity (an ACAD_TABLE's 342 TABLESTYLE) INTO the skeleton's own stream:
  // fresh minted own handle (from the SAME minter as every other record, so it
  // can never alias one), `330` = the owning dictionary, a `3`/`350` child in
  // that dictionary. `carriedRemap` (old source handle → minted handle) is
  // applied to the entity references further down, beside the record-remint
  // follow pass — the referring 342 must land on the merged record or the
  // carry is worse than useless (a handle that now resolves to the WRONG kind
  // of object). Runs HERE so the merged record is already in `rawObjectGroups`
  // when the own-handle census (`collectRawOwnHandles`) and
  // `dropDanglingDictionaryKeys` run: otherwise the census would not see it and
  // the dictionary child we just added would be swept straight back out.
  const carriedMerge = mergeCarriedObjectRecords(
    rawObjectGroups,
    carriedObjectRecords,
    mintHandle,
  );
  rawObjectGroups = carriedMerge.rawObjectGroups;

  const blockRecords: Record<string, BlockRecord> = {
    ...reconciliation.blockRecords,
    ...userNamedRecords(structuredClone(document.blockRecords ?? {})),
    // T9b review round 3 (P1) — the synthesized `*D<n>` blocks' same-named
    // typed BLOCK_RECORDs enter the record set BEFORE `remintRecordOwnHandles`
    // / `wireSymbolTableOwners`, so they are minted, owned (`330` = BLOCK_RECORD
    // head) and counted like a user block record (the completeness fallback
    // below no longer has to synthesize them after the remint).
    ...anonymousDimBlocks.blockRecords,
  };
  const { layouts, ownerBySpaceId } = reconciliation;

  // NOTE — the ACAD_LAYOUT dictionary child-list rewrite (ROOT C) is DEFERRED
  // until AFTER `remintRecordOwnHandles` (below). `reconcilePaperSpaces` captured
  // `layoutDictEntries` from the PRE-remint layout handles; a layout with an
  // empty / non-hex / colliding handle is reminted by `remintRecordOwnHandles`,
  // and the emitted AcDbLayout object then carries the POST-remint handle. If we
  // patched the dict here with the PRE-remint handles, the dict's `350` child
  // would dangle (point at a stale/blank handle that no LAYOUT object emits) →
  // ezdxf "Invalid handle" / orphaned tab. `patchLayoutDictionaryEntries` is
  // therefore applied below from `finalLayouts` (the POST-remint handles).

  // ── ROOT B PASS 1 — assign a fresh hex handle PER ENTITY OCCURRENCE.
  //
  // Codex P2 #4 — a single old-id → handle map is UNSOUND when the same `id`
  // appears at TWO occurrences. The BLOCK dialog's RETAIN mode leaves the source
  // entity in model space AND stores a CLONE with the SAME `id` inside the new
  // block definition; a single id-keyed map would assign ONE handle to BOTH (the
  // later pass overwrites the earlier mapping) → duplicate group-5 handles →
  // strict ezdxf rejects the file. We therefore key the handle by the ENTITY
  // OBJECT IDENTITY (`occurrenceHandles`, distinct object per occurrence even
  // when ids collide) so the model entity and its retained in-block clone get
  // DISTINCT handles.
  //
  // For sibling-reference rewriting we still need an old-id → new-hex lookup, but
  // SCOPED to the occurrence so a cross-ref resolves to the CORRECT one: a
  // model-space ref → the model entity's handle; an in-block clone's ref → the
  // clone's handle. We keep one `topLevelIdMap` (model + paper SPACE entities)
  // and one `blockIdMaps` entry per user block (its children). When the same id
  // exists at both, the block-scoped lookup wins INSIDE that block; the top-level
  // lookup wins for top-level entities.
  let maxHandle = ENTITY_HANDLE_BASE;
  // T9b review round 1 (P2) — ONE handle PER OCCURRENCE, queued per object: the
  // SAME entity object at two array positions (identity, not just id) must emit
  // two DISTINCT group-5 handles, so PASS 2 consumes the queue in the same visit
  // order (`takeOccurrenceHandle`) instead of reading a last-writer-wins slot.
  const occurrenceHandles = new Map<object, string[]>();
  const allOccurrenceHandles: string[] = [];
  const takeOccurrenceHandle = (entity: object): string | undefined =>
    occurrenceHandles.get(entity)?.shift();
  const topLevelIdMap = new Map<string, string>();
  const blockIdMaps = new Map<string, Map<string, string>>();
  const remintOccurrence = (
    entity: object,
    oldId: string | undefined,
    scope: Map<string, string>,
  ): string => {
    const handle = mintHandle();
    const numeric = Number.parseInt(handle, 16);
    if (Number.isFinite(numeric) && numeric > maxHandle) maxHandle = numeric;
    const queue = occurrenceHandles.get(entity);
    if (queue) queue.push(handle);
    else occurrenceHandles.set(entity, [handle]);
    allOccurrenceHandles.push(handle);
    // Last-writer-wins WITHIN a scope is fine: two same-id occurrences in the
    // SAME scope are degenerate (the model never produces them), and the
    // per-occurrence `occurrenceHandles` already carries the authoritative
    // group-5 handle; the scope map only resolves sibling refs.
    //
    // Axis A2 — DXF handles are CASE-INSENSITIVE. Seed the occurrence id-map
    // LOWERCASED so a sibling ref in different casing ("a1" → an id "A1") still
    // resolves (the generic rewrite looks up `value.toLowerCase()`). Without this
    // the case-mismatched ref misses the map, stays hex → dangling.
    if (typeof oldId === "string" && oldId.length > 0) {
      scope.set(oldId.toLowerCase(), handle);
    }
    return handle;
  };
  for (const space of document.spaces) {
    // round-14 BUG 3 — MIRROR the exporter's space exclusion
    // (`dxfBridge.export.ts`: `kind !== "block-test" && kind !== "block-edit"`).
    // block-test (TESTBLOCK sandbox) + block-edit (BEDIT working canvas) spaces
    // are TRANSIENT and are NEVER emitted to the DXF. Walking their entities here
    // pollutes `topLevelIdMap` and MINTS hex handles (advancing `maxHandle` /
    // `$HANDSEED`) that NO emitted entity ever uses → shadow ids that could
    // shadow a real id in the scope map. Only emitted spaces contribute.
    if (space.kind === "block-test" || space.kind === "block-edit") continue;
    for (const entity of space.entities) {
      remintOccurrence(entity, entity.id, topLevelIdMap);
    }
  }
  // User block-definition children (the skeleton's structural blocks have none).
  // Each block gets its OWN scope map so an in-block clone's id is keyed
  // separately from the same-id model-space original.
  // T9b review round 2 (P2) — walk ONLY the blocks whose typed children the
  // BLOCKS-section export emits (`emittedBlockEntries`: the exporter's own
  // predicate). A `*Model_Space` / first-`*Paper_Space` / paired extra-tab block
  // that carries typed children has them DROPPED by the exporter (the SPACE is
  // authoritative), so minting handles for them advanced `$HANDSEED` on every
  // save for records that were never written.
  const emittedUserBlocks = new Map(emittedBlockEntries(document));
  for (const [name, block] of emittedUserBlocks) {
    const scope = new Map<string, string>();
    blockIdMaps.set(name, scope);
    for (const child of block.entities) {
      const childId = (child as { id?: string }).id;
      const childHandle = remintOccurrence(child, childId, scope);
      // round-15 BUG 1 — a typed ATTDEF emits its group-5 from `handle` (NOT
      // `id`), and the conformant pass overwrites that group-5 with this
      // occurrence handle. A sibling 1005/attDefHandle ref can carry the ATTDEF's
      // ORIGINAL `handle` value (distinct from its `id`), so ALSO key the original
      // own handle → the occurrence handle. Seeding it HERE (in PASS 1, before
      // `composeScope` snapshots the block scope) makes the ref resolve in BOTH
      // entity orders (ref-before / ref-after the ATTDEF) AND in BOTH the typed
      // block-child path (line ~873) and the raw 1005 path (line ~1237) — each
      // rebuilds its scope from THIS underlying map. The per-iteration copy is
      // additionally re-pointed after the generic rewrite (which clobbers this
      // entry with a throwaway) so an after-ref in the typed loop also follows.
      const attdefOwnHandle = (child as { handle?: string; type?: string });
      if (
        attdefOwnHandle.type === "Attdef" &&
        typeof attdefOwnHandle.handle === "string" &&
        attdefOwnHandle.handle.length > 0
      ) {
        scope.set(attdefOwnHandle.handle.toLowerCase(), childHandle);
      }
    }
  }

  // Build the sibling-ref resolution map for a given scope: block children
  // resolve against their block's scope FIRST (the in-block clone's refs → the
  // clone's handles), falling back to the top-level map for any ref that points
  // at a model-space sibling; top-level entities use the top-level map alone.
  const composeScope = (
    blockScope?: Map<string, string>,
  ): ReadonlyMap<string, string> => {
    if (!blockScope || blockScope.size === 0) return topLevelIdMap;
    const merged = new Map(topLevelIdMap);
    for (const [k, v] of blockScope) merged.set(k, v); // block wins within block.
    return merged;
  };

  // ── round-15 BUG 3 — RAW OWN-HANDLE REMINT (PASS 1) hoisted BEFORE the typed
  //    PASS 2 so a TYPED entity's XData 1005 ref to a RAW unsupported sibling
  //    resolves DIRECTLY through the occurrence id-maps. ──
  //
  // The raw remint used to run AFTER the typed PASS 2. A typed entity's 1005 ref
  // to a raw sibling therefore could not resolve yet: `rewriteEntityHandlesGeneric`
  // NULLED + DROPPED a non-hex ref (a dangling-looking value) and kept a hex ref
  // VERBATIM at its OLD value. The raw remint then MOVED the raw sibling's own
  // group-5 (recorded old→new in `rawOwnRemap`) → the typed 1005 dangled (hex) or
  // was already gone (non-hex). Minting the raw OWN handles HERE and seeding the
  // old→new into the occurrence id-maps makes the typed 1005 ref resolve in PASS 2
  // exactly like a typed→typed sibling ref — never dropped, never stale. The
  // shared `mintHandle` reserves every minted handle GLOBALLY, so a record handle
  // reminted later can never alias one of these. The raw-stream 1005 refs (raw→
  // typed / raw→raw) are still rewritten in the LATE PASS-2 block below.
  //
  // The raw-group ARRAYS are stable references: `blocks[name].rawEntityGroups`
  // (from `structuredClone(document.blocks)` at the `blocks` build) survives the
  // shallow `{...block, …}` spreads through the child-remint loop AND
  // `recordRemint.blocks`, so a mutation here persists onto `finalBlocks`. The
  // top-level stream is cloned once into `rawDocGroups` and emitted directly.
  const rawDocGroups = structuredClone(document.rawEntityGroups ?? []);
  // Collision seed for the raw own handles — the broad document+skeleton handle
  // population already collected for the minter, plus the reminted occurrence
  // handles. (Record/block/layout handles are reminted LATER off the same shared
  // minter, which reserves globally, so they can never alias these.)
  const rawUsed = new Set<string>(seedHandles);
  for (const h of allOccurrenceHandles) rawUsed.add(h.toLowerCase());
  // Shared old→new own-handle map across the document + every block raw stream so
  // a cross-stream XData 1005 reference resolves.
  const rawOwnRemap = new Map<string, string>();
  // Top-level raw stream: its raw children are siblings of the model/paper SPACE
  // entities → seed the TOP-LEVEL occurrence map with its old→new mappings.
  remintRawEntityGroupHandles(
    rawDocGroups,
    topLevelIdMap,
    rawUsed,
    rawOwnRemap,
    mintHandle,
  );
  // Per-block raw streams: their raw children are siblings of the block's TYPED
  // children → mint on the stable `blocks[name].rawEntityGroups` array and seed
  // the BLOCK scope with the resulting old→new so a typed block child's 1005 ref
  // to a raw block sibling resolves through the composed block scope.
  for (const block of Object.values(blocks)) {
    const raw = (block as { rawEntityGroups?: RawEntityGroupLike[] })
      .rawEntityGroups;
    if (!raw) continue;
    remintRawEntityGroupHandles(raw, topLevelIdMap, rawUsed, rawOwnRemap, mintHandle);
  }
  // XData 1005 handles are document-global, so seed EVERY block scope (and the
  // top-level map) with the FULL accumulated raw old→new map — a typed block
  // child's 1005 ref to a raw sibling in ANY stream then resolves through its
  // composed block scope, exactly like a typed→typed sibling ref.
  for (const [k, v] of rawOwnRemap) topLevelIdMap.set(k, v);
  for (const blockScope of blockIdMaps.values()) {
    for (const [k, v] of rawOwnRemap) blockScope.set(k, v);
  }
  // Keep the LATER record-remint collision seed aware of the early raw own handles
  // (defensive — raw handles are high-range mints, user record handles low, so a
  // collision is practically impossible, but the seed must be complete).
  const rawMintedHandles = new Set<string>(rawOwnRemap.values());

  // ── ROOT B PASS 2 — clone each entity, stamp its per-occurrence hex handle
  // (`id`), wire its owner BLOCK_RECORD (group 330) to ITS OWN space's record,
  // then rewrite every sibling-pointing handle field through the TOP-LEVEL scope
  // (a non-hex ref with no map entry is DROPPED, never emitted as an invalid
  // non-hex handle).
  const spaces: DrawingSpace[] = document.spaces.map((space) => {
    // round-14 BUG 3 — mirror PASS-1: a block-test / block-edit (transient,
    // never-emitted) space got NO occurrence handles in PASS-1, so re-minting it
    // here would deref `occurrenceHandles.get(entity)!` to `undefined` (corrupt
    // id). Carry the transient space through untouched — the exporter excludes it
    // anyway, so its ids do not need (and must not consume) hex handles.
    if (space.kind === "block-test" || space.kind === "block-edit") {
      return structuredClone(space);
    }
    const ownerHandle = ownerBySpaceId.get(space.id);
    const entities: DrawingEntity[] = space.entities.map((entity) => {
      const handle = takeOccurrenceHandle(entity)!;
      const next: DrawingEntity = {
        ...structuredClone(entity),
        // The writer reads `entity.id` as the DXF group-5 handle; re-mint it to
        // a valid uppercase-hex handle (was a non-hex id like "line-1").
        id: handle,
      };
      // Wire DXF group 330 (owner BLOCK_RECORD) so the ENTITIES section records
      // resolve to a real owner. Only set when a record handle is known.
      if (ownerHandle) next.ownerHandle = ownerHandle;
      // GENERIC structural rewrite (defects #1/#2/#4) — rewrite EVERY handle
      // field (`/Handle$/` string, `/Handles$/` string[], `{code,handle}[]`,
      // XData 1005) on the deep-cloned entity through the top-level id map. This
      // single traversal closes clippingBoundaryHandle (340), the entire MLEADER
      // family (blockRecordHandle/attDefHandle/styleHandle/lineTypeHandle on
      // contextData/leaderRoots/secondaryContexts), INSERT attributes[].handle,
      // frozenLayerHandles + layerOverrideHandles — immune to path-shape drift.
      // The entity's own `id` + `ownerHandle` (set above) are skipped at the top
      // level by the walk.
      //
      // FIX A (round-9 #1) — pass the shared `mintHandle` so a NESTED OWN group-5
      // handle (an INSERT `attributes[].handle` ATTRIB) that is empty / non-hex
      // and absent from the occurrence idMap is reminted to a FRESH hex instead
      // of being DROPPED to a handle-less ATTRIB (which strict ezdxf rejects).
      rewriteEntityHandlesGeneric(next, topLevelIdMap, mintHandle);
      stampEntityOwnRecords(next, entity, anonymousDimBlocks, mintHandle);
      return next;
    });
    return { ...structuredClone(space), entities };
  });

  // ── ROOT B PASS 2 (blocks) — re-mint every user block-definition child's
  // handle through its PER-OCCURRENCE handle so the BLOCKS section carries valid
  // hex group-5 handles DISTINCT from a same-id model-space original (Codex P2
  // #4), and rewrite the children's sibling refs through the block-scoped map
  // (in-block clone refs → clone handles, model refs → model handles). `blocks`
  // already merged the user's blocks over the skeleton's; deep-clone + re-stamp.
  for (const [name, block] of Object.entries(blocks)) {
    const userBlock = emittedUserBlocks.get(name);
    if (!userBlock) continue; // skeleton structural block / non-emitted children — nothing to remint.
    const blockScope = composeScope(blockIdMaps.get(name));
    const remintedChildren = userBlock.entities.map((sourceChild, index) => {
      // Clone the EMITTED child (already merged into `blocks`) but resolve its
      // per-occurrence handle from the SOURCE child object (the one PASS 1
      // keyed). `blocks[name]` is `{...userBlock}` so child order is identical.
      const emittedChild = block.entities[index] ?? sourceChild;
      const c = structuredClone(emittedChild) as {
        id?: string;
        type?: string;
        handle?: string;
      };
      const handle = takeOccurrenceHandle(sourceChild);
      if (handle !== undefined) c.id = handle;
      // BUG 1 (round-13) / BUG 4 (round-14) — a typed ATTDEF child emits its
      // group-5 from `handle` (`drawingAttdefToDxfAttdef` writes
      // `common.handle = attdef.handle`), NOT from `id`. PASS 1 keyed the
      // occurrence handle under the SOURCE child's `id` in the scope map, so EVERY
      // sibling ref to this ATTDEF (an MLEADER `attDefHandle`, an XData 1005) is
      // rewritten to that occurrence `handle` — REGARDLESS of where the ref sits
      // relative to the ATTDEF child in the block's entity order.
      //
      // round-13 fixed only the BLANK case (a from-scratch `createAttdefMutation`
      // ATTDEF starts with an empty `handle`; the generic rewrite mints a FRESH
      // hex → emitted group-5 ≠ the occurrence handle the refs resolve to). But an
      // EXISTING (non-blank) hex ATTDEF handle has the SAME divergence: the generic
      // rewrite below collision-checks `c.handle` and may RE-MINT it away from a
      // skeleton-colliding value → the emitted group-5 ≠ the occurrence `handle`,
      // so a ref processed BEFORE the ATTDEF (rewritten via the scope map to the
      // occurrence handle) DANGLES against the ATTDEF's reminted group-5.
      //
      // round-14 BUG 4 — align the ATTDEF's own `handle` with the occurrence
      // handle (== `c.id`) for ALL ATTDEFs (blank OR hex). The occurrence handle is
      // the AUTHORITATIVE one PASS 1 reserved + every sibling ref resolves to, so
      // the emitted ATTDEF group-5 must equal it. Done AFTER the generic rewrite
      // (so the rewrite of the ATTDEF's OTHER sibling-ref fields still runs);
      // pre-seeding the handle would read as a hex COLLISION to the rewrite's
      // reserve() and be reminted to yet another fresh hex — re-introducing the
      // divergence.
      const isAttdef = c.type === "Attdef";
      // round-15 BUG 1 — capture the ATTDEF's ORIGINAL own group-5 (BEFORE the
      // generic rewrite) so the scope-map entry the rewrite records for it can be
      // re-pointed at the FINAL emitted handle below. `drawingAttdefToDxfAttdef`
      // emits the group-5 from `attdef.handle`; the generic rewrite's FIX A mints
      // a THROWAWAY hex for an empty/non-hex/colliding `handle` and records
      // `originalOwnHandle → throwaway` into `blockScope`. But line ~918 then
      // overwrites the emitted group-5 with the AUTHORITATIVE occurrence `handle`,
      // leaving the scope entry pointing at the throwaway that is NEVER emitted →
      // a sibling 1005/attDefHandle ref carrying the ATTDEF's ORIGINAL handle
      // resolves to that throwaway → DANGLING. Capture both keys to re-point.
      const attdefOriginalOwnHandle =
        isAttdef && typeof c.handle === "string"
          ? c.handle.toLowerCase()
          : undefined;
      // GENERIC structural rewrite (defects #1/#2/#4) through the block-scoped
      // map (in-block clone refs → clone handles, model refs → model handles).
      // FIX A — mint a fresh hex for any nested OWN group-5 (e.g. an ATTDEF child
      // `handle` or a block-INSERT `attributes[].handle`) that is empty/non-hex.
      rewriteEntityHandlesGeneric(c, blockScope, mintHandle);
      if (handle !== undefined && isAttdef) {
        // The THROWAWAY handle FIX A minted for this ATTDEF's own group-5 (the
        // rewrite leaves it on `c.handle`); after we overwrite `c.handle` with the
        // occurrence handle it is emitted by NO record, so every scope entry that
        // points at it must be re-pointed to the occurrence handle.
        const throwaway =
          typeof c.handle === "string" ? c.handle.toLowerCase() : undefined;
        c.handle = handle; // align emitted group-5 with `id` + the rewritten refs
        // round-15 BUG 1 — keep `blockScope` aligned with the FINAL `c.handle`.
        // `blockScope` is a `ReadonlyMap` only by signature (the generic rewrite's
        // FIX A already mutates it via a `Map` cast); re-point it the same way so a
        // sibling ref by the ATTDEF's ORIGINAL handle — processed BEFORE or AFTER
        // the ATTDEF in entity order — resolves to the emitted occurrence handle.
        const scope = blockScope as Map<string, string>;
        // (1) Re-point every entry currently aimed at the throwaway (the original
        //     own handle's entry, plus any other key FIX A pointed there).
        if (throwaway !== undefined && throwaway !== handle.toLowerCase()) {
          for (const [k, v] of scope) {
            if (v.toLowerCase() === throwaway) scope.set(k, handle);
          }
        }
        // (2) Add the ORIGINAL-own-handle → occurrence-handle mapping explicitly
        //     (blank/non-hex originals leave no FIX-A entry to catch in (1)).
        if (
          attdefOriginalOwnHandle !== undefined &&
          attdefOriginalOwnHandle.length > 0
        ) {
          scope.set(attdefOriginalOwnHandle, handle);
        }
      }
      stampEntityOwnRecords(c, sourceChild, anonymousDimBlocks, mintHandle);
      return c;
    });
    blocks[name] = {
      ...block,
      entities: remintedChildren as typeof block.entities,
    };
  }

  // ── RECORD-OWN-HANDLE MINTER (defects #3 / #5) ──
  // Mint / collision-check every RECORD-own group-5 handle (`blocks[*].handle`,
  // `blockRecords[*].handle`, `layouts[*].handle`, LAYER record handles) against
  // the skeleton structural handles + every reminted entity handle. This closes:
  //   #3 — a from-scratch user BLOCK / BLOCK_RECORD with an EMPTY handle (the
  //        writer emits group-5 only `if(handle)` → strict ezdxf rejects a
  //        BLOCK / BLOCK_RECORD with no handle).
  //   #5 — an INSERT-merged imported LAYOUT / style / record carrying a HEX
  //        source handle that COLLIDES with a skeleton structural handle (kept
  //        verbatim before = duplicate group-5).
  // The reserved `*Model_Space` / `*Paper_Space` records are PRESERVED verbatim
  // (their handles are skeleton-owned + already wired into the per-space entity
  // owner handles, so they must not move). The shared `mintHandle` keeps a
  // minted record handle disjoint from every entity handle.
  const usedForRecords = new Set(skeletonHandles);
  for (const h of allOccurrenceHandles) usedForRecords.add(h.toLowerCase());
  // round-15 BUG 3 — the raw OWN handles were minted EARLY (hoisted before PASS 2)
  // off the shared minter, which reserves globally so a record handle can never
  // alias one. Seed the record-remint collision set with them too so a kept-
  // verbatim USER record handle can never be left equal to an early raw handle
  // (defensive; raw handles are high-range mints, user record handles low).
  for (const h of rawMintedHandles) usedForRecords.add(h.toLowerCase());
  // Same reasoning for a CARRIED OBJECTS record's minted own handle: it comes
  // off the shared `mintHandle` (so nothing is ever MINTED onto it), but a
  // record whose handle is KEPT verbatim is judged against THIS set, not the
  // minter — so a copied source handle that happened to equal one of these
  // would be left in place and duplicate the carried record's group 5.
  for (const h of carriedMerge.carriedOwnHandles) usedForRecords.add(h);
  // BUG #550 (collision-seed completeness) — SEED the record-collision set with
  // every skeleton BLOCK definition's own group-5 header handle (`*Model_Space`
  // = "18", `*Paper_Space` = "1C"). These are DISTINCT from the BLOCK_RECORD
  // handles ("17"/"1B", already in `skeletonHandles` via `collectSkeletonHandles`'
  // typed blockRecord loop) and live ONLY in the typed `skeleton.blocks` map —
  // `collectSkeletonHandles` walks the raw group streams + typed blockRecords +
  // layouts but NEVER `skeleton.blocks`, so the BLOCK header handle was absent
  // from the seed. `remintRecordOwnHandles` visits USER records BEFORE the
  // reserved skeleton blocks, so a USER/INSERT-merged record whose handle equals
  // a skeleton BLOCK header (e.g. "18") was treated as UNUSED + KEPT; the skeleton
  // BLOCK later emits that same group-5 → DUPLICATE object handles → strict ezdxf
  // rejects. `collectHandlesDeep` harvests `block.handle` (and any nested handle)
  // so the colliding user record is reminted away. (blockRecords are already
  // covered; this is belt-and-suspenders for both via the shared census leaf.)
  collectHandlesDeep(skeleton.blocks, usedForRecords);
  collectHandlesDeep(skeleton.blockRecords, usedForRecords);
  // FINDING 2 — SEED the record-remint collision set with the adopted TABLE-HEAD
  // handles (`symbolTableHeaders` LAYER/STYLE/DIMSTYLE/BLOCK_RECORD = 5/4/9,
  // `linetypeTableHeader` LTYPE = 2, etc.). These are LOW hex handles the from-
  // scratch doc ADOPTS from the skeleton template and EMITS as real `5/<hex>`
  // table heads, yet `collectSkeletonHandles` (the source of `skeletonHandles`)
  // never walks these two fields — it harvests the raw streams + typed
  // blockRecords + layouts only. So a from-scratch USER block / block-record /
  // layout / layer carrying a valid low hex handle EQUAL to a table-head handle
  // (e.g. "5") was treated as UNUSED + KEPT verbatim by `remintRecordOwnHandles`
  // → the export then emits BOTH that record's group-5 AND the table head's
  // group-5 = DUPLICATE handle → strict ezdxf / AutoCAD reject. Seeding them here
  // (mirrors the entity-minter `seedHandles` seed at the top via the same
  // `collectHandlesDeep`) makes a colliding record handle remint away from the
  // table-head handles. `remintRecordOwnHandles` covers ALL record classes
  // (blocks, block-records, layouts, layers/styles via `remintStyleTableHandles`),
  // so this single seed protects every one of them.
  collectHandlesDeep(skeleton.symbolTableHeaders, usedForRecords);
  collectHandlesDeep(skeleton.linetypeTableHeader, usedForRecords);
  // The handles that LEGITIMATELY belong to a SKELETON-SOURCED record (must not
  // be reminted as self-collisions): the reconciliation's block-records (skeleton
  // space records + minted `*Paper_Space<N>`) + the SKELETON's OWN layouts
  // (`Model` + the phantom `Layout1`). A USER record handle that merely EQUALS a
  // skeleton STRUCTURAL (table-head / dictionary) handle is NOT here → it still
  // collides. Used ONLY for the `used`-seed self-collision SUBTRACTION inside the
  // minter (so a skeleton/reconciled record's OWN handle is not seen as "used
  // elsewhere" and reminted) — NOT for the remap-exclusion (the narrow set below).
  //
  // BUG 5 (round-11 P2) — the LAYOUT contribution was previously taken from ALL
  // reconciled `layouts`, which INCLUDES reconciled USER/imported layouts. A USER
  // layout that kept a LOW hex handle equal to a skeleton STRUCTURAL handle (e.g.
  // "8", a VPORT/table-head handle in `usedForRecords` via the skeleton raw seed)
  // was then SUBTRACTED from `used` → its handle was seen as FREE → KEPT verbatim →
  // DUPLICATE group-5 with the skeleton structural record. The fix narrows the
  // subtraction set's layout arm to the SKELETON document's OWN layouts only, so a
  // user layout colliding with a skeleton structural handle IS reminted away. A
  // user layout whose handle does NOT collide is never in `usedForRecords`, so it
  // needs no subtraction; a reconciliation-MINTED layout handle is in the disjoint
  // high range (minted AFTER the `usedForRecords` seed) so it is likewise never a
  // self-collision. The block-record arm stays from `reconciliation.blockRecords`
  // (skeleton spaces + MINTED `*Paper_Space<N>` — every minted record gets a fresh
  // hex via `mintHandle()`, never a colliding low user handle).
  const skeletonRecordHandles = new Set<string>();
  for (const r of Object.values(reconciliation.blockRecords)) {
    if (r.handle) skeletonRecordHandles.add(r.handle.toLowerCase());
  }
  for (const name of [MODEL_LAYOUT_NAME, SKELETON_PAPER_LAYOUT_NAME]) {
    const h = skeleton.layouts?.[name]?.handle;
    if (h) skeletonRecordHandles.add(h.toLowerCase());
  }
  // BUG #849 (remap-exclusion precision) — the set of handles a NON-USER (skeleton)
  // reference legitimately points AT, which must be EXCLUDED from the remint
  // `remap` so a colliding USER record relocates WITHOUT re-homing the skeleton's
  // inbound refs onto it. This is the TRUE skeleton-OWNED set, taken from the
  // SKELETON document directly (NOT from `reconciliation`, which also contains
  // minted `*Paper_Space<N>` records + reconciled USER layouts):
  //   - the skeleton SPACE-record handles (`*Model_Space` = "17", `*Paper_Space`
  //     = "1B") — every model/paper ENTITY's `ownerHandle` (330) points at these
  //     (case (a): the entities must keep owning the skeleton space, so a colliding
  //     user record relocates but the 330 refs stay → EXCLUDE from remap);
  //   - the TRUE skeleton LAYOUT handles (`Model` = "1A" + the phantom `Layout1`
  //     = "1E" that reconciliation replaces) — the skeleton's own LAYOUT↔BLOCK_RECORD
  //     pairing points at these (case (b): a user layout colliding with the
  //     SKELETON layout relocates while the skeleton layout keeps its pairing →
  //     EXCLUDE).
  // It does NOT include the reconciliation-MINTED `*Paper_Space<N>` record handles
  // NOR the reconciled USER layout handles — a USER record colliding with one of
  // THOSE still needs its old→new in the remap so its OWN owning BLOCK_RECORD
  // `layoutHandle` (340, set to the OLD value pre-remint) FOLLOWS it (BUG #849:
  // the broad `skeletonRecordHandles` suppressed this remap → orphaned paper
  // layout). It also does NOT include skeleton table-head / structural handles a
  // USER self-ref (`annotativeDecompositions`, BLOCK `ownerHandle`) targets — those
  // must still register + follow (AXIS-B regression guard).
  const skeletonOwnedRefHandles = new Set<string>();
  for (const name of [MODEL_SPACE_BLOCK_RECORD, PAPER_SPACE_BLOCK_RECORD]) {
    const h = skeleton.blockRecords?.[name]?.handle;
    if (h) skeletonOwnedRefHandles.add(h.toLowerCase());
  }
  for (const name of [MODEL_LAYOUT_NAME, SKELETON_PAPER_LAYOUT_NAME]) {
    const h = skeleton.layouts?.[name]?.handle;
    if (h) skeletonOwnedRefHandles.add(h.toLowerCase());
  }
  // T9b review round 1 (P2) — a text style referenced ONLY by name (an MLEADER
  // `textStyleName`, a referenced MLEADERSTYLE `342`) is materialised in the
  // typed tables HERE, before the remint, so its STYLE record is minted, owned
  // and emitted with the full payload like any other record (the export's
  // late-append path produced a bare, owner-less record).
  const styleTablesWithReferencedTextStyles = materializeReferencedTextStyles(document);
  const recordRemint = remintRecordOwnHandles(
    blocks,
    blockRecords,
    layouts,
    styleTablesWithReferencedTextStyles,
    usedForRecords,
    mintHandle,
    // The `used`-seed self-collision subtraction set (broad — skeleton + minted +
    // reconciled records/layouts) so a skeleton/reconciled record's OWN handle is
    // not seen as a self-collision and reminted.
    skeletonRecordHandles,
    // BUG #849 — the NARROW remap-exclusion set (skeleton-OWNED space-record +
    // layout handles only). A USER record colliding with one of these relocates
    // but its OLD value's inbound SKELETON refs stay; a USER record colliding with
    // a MINTED/reconciled handle (NOT in this set) registers its remap so its OWN
    // owning back-ref follows.
    skeletonOwnedRefHandles,
    // T9b — the LTYPE records are minted through the SAME `ensure` / used-set
    // (a seeded standard linetype carries no handle; R13+ requires one on every
    // symbol-table record) so a linetype handle can never alias another record.
    document.linetypes,
  );
  const finalBlocks = recordRemint.blocks;
  const finalBlockRecords = recordRemint.blockRecords;
  const finalLayouts = recordRemint.layouts;
  const finalStyleTables = recordRemint.styleTables;
  const finalLinetypes = recordRemint.linetypes ?? document.linetypes;
  // BUG #849 (deeper root — UNAMBIGUOUS LAYOUT↔BLOCK_RECORD re-pairing) — the
  // remint `remap` is keyed by OLD handle, so it is AMBIGUOUS when two records
  // shared an old handle (a malformed doc with two user LAYOUTs carrying the
  // SAME hex group-5: the one kept verbatim and the one reminted both have old
  // handle "ABC"; the global `remap["ABC"→new]` then over-rewrites the KEPT
  // layout's owning `record.layoutHandle` too). The LAYOUT↔BLOCK_RECORD pairing is
  // 1:1 and carried bidirectionally — every emitted LAYOUT knows its owning record
  // via `ownerBlockRecordHandle` (330) — so we re-derive each owning record's
  // `layoutHandle` (340) DIRECTLY from the FINAL layouts, immune to remap key
  // ambiguity. This is purely corrective for layout-owning records (a record no
  // layout points at is left untouched), and reproduces the skeleton's own
  // Model↔*Model_Space / paper-tab pairings verbatim.
  const recordByHandle = new Map<string, BlockRecord>();
  for (const record of Object.values(finalBlockRecords)) {
    if (record.handle) recordByHandle.set(record.handle.toLowerCase(), record);
  }
  for (const layout of Object.values(finalLayouts)) {
    const ownerHandle = layout.ownerBlockRecordHandle;
    if (!ownerHandle || !layout.handle) continue;
    const owner = recordByHandle.get(ownerHandle.toLowerCase());
    // Only re-pair when the owning record's `layoutHandle` is STALE (does not
    // already point at this layout) — a no-op for the already-correct common case.
    if (owner && owner.layoutHandle !== layout.handle) {
      owner.layoutHandle = layout.handle;
    }
  }
  // When a USER record was reminted (empty/non-hex/collision), follow it from
  // any ENTITY handle ref that targeted its old handle (e.g. an MLEADER 344
  // `blockRecordHandle` pointing at a reminted user BLOCK_RECORD). The entity
  // rewrite preserved 'table'/'object' refs verbatim, so a reminted record would
  // otherwise dangle. Rare (remap is empty unless a record handle actually
  // moved), so the substitution walk is guarded on `remap.size`.
  //
  // The CARRIED-OBJECT remap rides the SAME pass, because it is the same
  // problem: a record moved to a new handle and the entity references that
  // pointed at the old one must follow, or they dangle. An `ACAD_TABLE`'s 342
  // holds the SOURCE drawing's TABLESTYLE handle, which is hex and therefore
  // survives the entity rewrite verbatim (`rewriteOneHandle` keeps a hex
  // structural ref); without this substitution it would keep pointing at a
  // handle that means something else — or nothing — in the new file. Carried
  // entries are applied LAST so a (pathological) source document that reused one
  // handle for both a symbol-table record and an OBJECTS record resolves in
  // favour of the record we actually merged.
  const finalSpaces = spaces;
  const refRemap =
    carriedMerge.remap.size > 0
      ? new Map([...recordRemint.remap, ...carriedMerge.remap])
      : recordRemint.remap;
  if (refRemap.size > 0) {
    for (const space of finalSpaces) {
      for (const entity of space.entities) {
        applyHandleRemap(entity, refRemap);
      }
    }
    for (const block of Object.values(finalBlocks)) {
      const entities = (block as { entities?: unknown[] }).entities;
      if (Array.isArray(entities)) {
        for (const child of entities) applyHandleRemap(child, refRemap);
      }
    }
  }

  // ── BLOCK ⇄ BLOCK_RECORD COMPLETENESS PASS (FIX #2 + #4) ──
  // Runs AFTER `remintRecordOwnHandles` so `finalBlocks`/`finalBlockRecords`
  // handles are FINAL. For every NON-layout (user) BLOCK it guarantees the
  // BLOCK ⇄ BLOCK_RECORD bijection the writer relies on:
  //   (#4) a from-scratch / BEDIT block can exist in `document.blocks` with NO
  //        matching `document.blockRecords` entry (the createIfMissing path):
  //        synthesize a BLOCK_RECORD (mirroring `mintAdditionalPaperRecord`'s
  //        shape) so the emitted BLOCK_RECORD table is not missing it (an INSERT
  //        referencing the BLOCK would otherwise resolve to no record). T9b
  //        review round 3: the anonymous `*D<n>` dimension blocks arrive WITH
  //        their typed record (dimBlocks.ts) and never take this branch — it is
  //        the defensive fallback for a record-less user block only.
  //   (#2) a from-scratch / BEDIT block has a BLANK `block.ownerHandle`, so
  //        `serializeBlockDefinition` emits NO group-330 → the BLOCK is not owned
  //        by its BLOCK_RECORD. Wire `block.ownerHandle` (330) ⇄ the record's
  //        handle (5). Also mint the BLOCK's own group-5 if it is still non-hex.
  // The skeleton's `*Model_Space`/`*Paper_Space[N]` are SKELETON-owned (their 330
  // wiring is correct already) so they are skipped via `isLayoutBlockName`. The
  // SHARED `mintHandle` keeps synthesized handles disjoint; the `seedCeiling`
  // computation below reads `finalBlockRecords` AFTER this pass so `$HANDSEED`
  // covers any synthesized record handle.
  for (const [name, block] of Object.entries(finalBlocks)) {
    if (isLayoutBlockName(name)) continue;
    const blk = block as { handle?: string; ownerHandle?: string };
    let record = finalBlockRecords[name];
    if (record === undefined) {
      // (#4) — no BLOCK_RECORD for this user BLOCK: synthesize one (same shape
      // `mintAdditionalPaperRecord` uses for a fresh paper record).
      record = {
        name,
        handle: mintHandle(),
        insertionUnits: 0,
        explodable: true,
        scalable: "non-uniform",
        isAnnotative: false,
        annotativeDecompositions: {},
        description: "",
      } satisfies BlockRecord;
      finalBlockRecords[name] = record;
    } else if (!isHexHandle(record.handle ?? "")) {
      // Defensive — `remintRecordOwnHandles` already mints a hex record handle,
      // but a synthesized/edge-case record without one must own a valid group-5
      // before the BLOCK's 330 is wired to it.
      record = { ...record, handle: mintHandle() };
      finalBlockRecords[name] = record;
    }
    // (#2) — the BLOCK's own group-5 must be valid hex before it is emitted.
    if (!isHexHandle(blk.handle ?? "")) blk.handle = mintHandle();
    // (#2) — wire the BLOCK's 330 owner to its BLOCK_RECORD's handle (5).
    blk.ownerHandle = record.handle;
  }

  // ── RAW ENTITY-GROUP 1005 SIBLING-REF REWRITE (FIX C — round-9 #3 / SG-2/SG-4;
  //    round-15 BUG 3) — the raw OWN handles were already minted ABOVE (hoisted
  //    before the typed PASS 2 so typed→raw 1005 refs resolve), recording old→new
  //    in `rawOwnRemap`. Here we rewrite the raw streams' OWN 1005 sibling refs now
  //    that every own handle (typed + raw) is final, so a forward/back reference
  //    to ANY reminted child resolves. The block raw streams on `finalBlocks` are
  //    the SAME stable arrays mutated by the hoisted minting (the shallow record/
  //    child-remint spreads preserve `rawEntityGroups` by reference).
  //
  // The TOP-LEVEL `document.rawEntityGroups` (`rawDocGroups`) resolves its 1005
  // refs against the TOP-LEVEL occurrence map (its raw children are siblings of
  // the model/paper SPACE entities).
  //
  // round-16 BUG 3 — thread the STRUCTURAL OWNER handle so a raw entity's FIRST
  // 330 (its owner) that does not resolve via the remint maps is set to the real
  // owning record (never "0" → orphan). The top-level ENTITIES stream's children
  // are siblings of the MODEL-space entities, so their structural owner is the
  // `*Model_Space` BLOCK_RECORD handle (final, post-remint).
  const modelSpaceOwnerHandle = finalBlockRecords[MODEL_SPACE_BLOCK_RECORD]?.handle;
  rewriteRawEntityGroupRefs(
    rawDocGroups,
    topLevelIdMap,
    rawOwnRemap,
    modelSpaceOwnerHandle,
  );
  // BUG 4 (round-10) — a BLOCK's raw groups resolve their 1005 XData refs against
  // the COMPOSED BLOCK SCOPE (the block's child idMap composed with the top-level
  // map), EXACTLY like the block's TYPED children use `composeScope(blockScope)`.
  // Those 1005 refs can point at a TYPED sibling in the SAME block — a non-hex
  // block-sibling id is in the block-scoped idMap, NOT the top-level one, so
  // rewriting through `topLevelIdMap` MISSED it and (being non-hex) nulled the
  // ref. Composing the block scope makes the block-sibling ref resolve to that
  // sibling's minted handle. Skeleton structural blocks have no child scope, so
  // `composeScope(undefined)` correctly falls back to the top-level map.
  //
  // round-16 BUG 3 — a block raw entity's FIRST 330 owner falls back to ITS OWN
  // block's BLOCK_RECORD handle (the structural owner for `block.rawEntityGroups`).
  for (const [name, b] of Object.entries(finalBlocks)) {
    const raw = (b as { rawEntityGroups?: RawEntityGroupLike[] }).rawEntityGroups;
    if (!raw) continue;
    const blockScope = composeScope(blockIdMaps.get(name));
    const blockRecordOwnerHandle = finalBlockRecords[name]?.handle;
    rewriteRawEntityGroupRefs(
      raw,
      blockScope,
      rawOwnRemap,
      blockRecordOwnerHandle,
    );
  }

  // ROOT C — rewrite the ACAD_LAYOUT dictionary's child list (in the verbatim
  // OBJECTS passthrough) to exactly the reconciled layouts, so the phantom
  // `Layout1` entry is removed and every minted paper tab is registered + owned.
  //
  // FIX 3 (P1, dangling handle) — recompute the dictionary entries from the
  // POST-remint `finalLayouts`, NOT the PRE-remint `reconciliation.layoutDictEntries`.
  // `remintRecordOwnHandles` re-mints any layout whose handle was empty / non-hex
  // / colliding (e.g. a leftover `document.layouts` entry cloned with a blank
  // `buildDefaultLayout`-shaped handle); the emitted AcDbLayout object carries the
  // POST-remint handle, so the dict's `350` child must carry the SAME handle or it
  // dangles → ezdxf "Invalid handle" / orphaned tab. `applyHandleRemap` does NOT
  // reach `rawObjectGroups` (it walks entities/block-children, and the dict child
  // is a `{code:350,value}` shape it doesn't match), so the dict MUST be patched
  // here from `finalLayouts`. The dict's OWN handle (`layoutDictHandle` = "D") is
  // stable — never reminted.
  if (reconciliation.layoutDictHandle) {
    const finalLayoutDictEntries = Object.values(finalLayouts).map((l) => ({
      name: l.name,
      handle: l.handle,
    }));
    rawObjectGroups = patchLayoutDictionaryEntries(
      rawObjectGroups,
      reconciliation.layoutDictHandle,
      finalLayoutDictEntries,
    );
  }

  // @codex round 18 P2 — FOLLOW THE RECORD REMINT INTO THE CARRIED RECORDS,
  // BEFORE the emitted-handle census below. A carried record's own pointers (a
  // TABLESTYLE cell style's `340` → a text STYLE) name SOURCE-drawing handles,
  // and `remintRecordOwnHandles` has just MOVED every record whose source handle
  // was empty / non-hex / colliding with a skeleton handle. Judging those stale
  // values against a census of POST-remint handles is wrong in both directions,
  // and both were measured on a WBLOCKed table:
  //   - the STYLE moved and nothing else claims the old handle →
  //     `dropDanglingCarriedPointers` DELETES a dependency that is present in
  //     the file under its new handle (source handle `5`);
  //   - the STYLE moved and the skeleton itself owns the old handle → the census
  //     counts it "resolved" and KEEPS the HARD pointer aimed at that other
  //     object (source handle `13` → the `Normal` ACDBPLACEHOLDER).
  // The fix is the ORDER, not a rule for group 340: the SAME 330–369 set the
  // prune judges is rewritten here first, so no pointer is ever judged on a
  // handle the export has moved.
  //
  // The map is `recordRemint.relocations` — EVERY relocation — and NOT the
  // narrow `refRemap` the entities followed. `remap` deliberately WITHHOLDS a
  // relocation whose OLD handle is a SKELETON-OWNED reference target
  // (`*Model_Space` `17`, the Model layout `1A` — BUG #849), because a SKELETON
  // reference legitimately keeps the old value. A carried record's pointer is
  // never a skeleton reference — it arrived from another drawing's object graph
  // — so withholding it left exactly this defect for those handles (measured: a
  // source STYLE at `1A` stayed at `1A`, i.e. at the skeleton's Model LAYOUT).
  // The carried merge's own source → minted entries ride along, so one carried
  // record pointing at another resolves too.
  const carriedPointerRemap =
    carriedMerge.remap.size > 0
      ? new Map([...recordRemint.relocations, ...carriedMerge.remap])
      : recordRemint.relocations;
  rawObjectGroups = remapCarriedPointers(
    rawObjectGroups,
    carriedMerge.carriedOwnHandles,
    carriedPointerRemap,
  );

  // FIX-202 — sweep DANGLING dictionary keys (a `3/name 350|360/handle` child
  // whose target object the export never emits). The skeleton template's
  // ACAD_MLEADERSTYLE dictionary references a `Standard` MLEADERSTYLE object that
  // the typed-MLEADERSTYLE import path strips out of `rawObjectGroups` (and the
  // from-scratch export re-emits no MLEADERSTYLE), so the `Standard → 350/<h>`
  // child dangles → ezdxf silently removes it → AutoCAD recovery. Build the set
  // of OWN handles the export WILL emit (every finalized structure + the verbatim
  // raw streams) and drop any dict child pointing OUTSIDE it. Runs after the
  // layout-dict patch so the re-pointed `350` layout children (which DO resolve to
  // emitted LAYOUT objects) are preserved.
  //
  // FINDING 3 — the census set must be OWN handles ONLY (the group-5 / 105 values
  // actually emitted as the own handle of a record / entity / block-record), NOT
  // every REFERENCE. The previous build used `collectHandlesDeep` (which harvests
  // every `*Handle` ref + `*Handles` array + raw 1005) and `OWN_HANDLE_GROUP_CODES`
  // (which includes 1005) — so a dangling dict child whose target ALSO appeared
  // merely as a REFERENCE somewhere (a 330/340/350/360 pointer, a `*Handle` field,
  // a 1005 xdata ref) was wrongly counted "resolved" → the dangling key was LEFT
  // IN → ezdxf re-repaired it (FIX-202 regression). The own-handle collectors
  // below take the FIRST 5/105 per raw record (never a reference code) and only
  // the bare `handle` key / entity `id` from the typed graph (never a `*Handle`
  // reference).
  const emittedHandles = new Set<string>();
  // Raw streams that round-trip verbatim into the OBJECTS / TABLES sections —
  // collect the FIRST 5/105 own handle per record (NOT references).
  collectRawOwnHandles(rawObjectGroups, emittedHandles);
  collectRawOwnHandles(rawTableGroups, emittedHandles);
  // HEADER / CLASSES passthrough (adopted verbatim from the skeleton below).
  for (const section of skeleton.rawPassthroughSections ?? []) {
    collectRawOwnHandles(section.groups, emittedHandles);
  }
  // The top-level ENTITIES raw stream (`document.rawEntityGroups` clone) is an
  // array of `{entityType, groups}` records — `collectTypedOwnHandles` recognises
  // each and harvests its FIRST 5/105 own handle (it routes raw records through
  // `collectRawOwnHandles`), so it is the right collector here (own handles only).
  collectTypedOwnHandles(rawDocGroups, emittedHandles);
  // Typed structures the export serializes — collect only their OWN group-5
  // handles (record `handle` + entity `id`), never their `*Handle` references.
  collectTypedOwnHandles(finalBlocks, emittedHandles);
  collectTypedOwnHandles(finalBlockRecords, emittedHandles);
  collectTypedOwnHandles(finalLayouts, emittedHandles);
  collectTypedOwnHandles(finalStyleTables, emittedHandles);
  // T9b — the LTYPE records' minted own handles are emitted `5 <h>` too.
  collectTypedOwnHandles(finalLinetypes, emittedHandles);
  collectTypedOwnHandles(finalSpaces, emittedHandles);
  // A CARRIED record arrives from ANOTHER drawing's object graph, so its own
  // pointers (a TABLESTYLE cell style's `340` → a text STYLE record) may name
  // handles this document never emits. Prune those before the dictionary sweep
  // below, scoped to the carried records alone — the skeleton's own stream is
  // internally consistent by construction and `dropDanglingDictionaryKeys` owns
  // the dictionary-child half of it. Carrying a style must not be a way to
  // smuggle IN a dangling pointer while closing one.
  rawObjectGroups = dropDanglingCarriedPointers(
    rawObjectGroups,
    carriedMerge.carriedOwnHandles,
    emittedHandles,
  );
  rawObjectGroups = dropDanglingDictionaryKeys(rawObjectGroups, emittedHandles);

  // T9b — wire every symbol-table RECORD's `330` owner to its adopted table
  // head (LAYER → head `1`, STYLE → `5`, DIMSTYLE → `4`, BLOCK_RECORD → `9`,
  // LTYPE → `2`) and give every LAYER its `390` PlotStyleName (the `Normal`
  // ACDBPLACEHOLDER) + `347` MATERIAL (`Global`) pointers, resolved from the
  // skeleton's OBJECTS stream — ezdxf `Layer.set_required_attributes`
  // (`plotstyle_handle` → `plotstyles["Normal"]`, `material_handle` →
  // `materials["Global"]`; the R2018 reference file carries `347 <Global>` on
  // layer `0`) and what AutoCAD writes on every R2007+ layer (a new layer's
  // material is `Global`; `ByLayer` / `ByBlock` are ENTITY-level materials).
  // A copied `390` / `347` survives ONLY when it targets an emitted object of
  // the RIGHT type (an ACDBPLACEHOLDER / a MATERIAL of the final OBJECTS stream
  // — T9b review round 1 P2: matching ANY emitted own handle let a layer point
  // at a LINE); otherwise it is replaced by the skeleton default (never left
  // dangling). The own-handle census above is unchanged (these are references,
  // not own handles). A head / target that is absent from the template leaves
  // the field unset — nothing is ever fabricated.
  const wiredTables = wireSymbolTableOwners({
    styleTables: finalStyleTables,
    blockRecords: finalBlockRecords,
    linetypes: finalLinetypes,
    heads: readSkeletonTableHeadHandles(skeleton),
    layerDefaults: {
      plotStyleHandle: findPlotStyleNormalHandle(rawObjectGroups),
      materialHandle: findMaterialHandleByName(rawObjectGroups, "Global"),
    },
    pointerTargets: collectLayerPointerTargets(rawObjectGroups),
  });

  // Adopt the skeleton's HEADER + CLASSES passthrough. $HANDSEED is patched to a
  // provisional ceiling here (entity handles + every own handle the census
  // above collected — records, layouts, T9b style/linetype/ENDBLK mints, raw
  // streams — + structural handles); the EXPORT path re-patches it to the
  // document-wide emitted-handle max AFTER the MLEADER / annotative minters run
  // (ROOT D), so this is a lower bound, not the final value.
  const seedCeiling = Math.max(
    maxHandle,
    maxHexHandleValue(skeletonHandles),
    maxHexHandleValue(emittedHandles),
  );
  const rawPassthroughSections = patchHandSeed(
    structuredClone(skeleton.rawPassthroughSections ?? []),
    seedCeiling,
  );

  return {
    ...document,
    spaces: finalSpaces,
    blockRecords: wiredTables.blockRecords,
    blocks: finalBlocks as DrawingDocument["blocks"],
    layouts: finalLayouts,
    styleTables: wiredTables.styleTables ?? document.styleTables,
    // T9b — the LTYPE table with minted record handles + wired owners.
    ...(wiredTables.linetypes ? { linetypes: wiredTables.linetypes } : {}),
    // FIX-110 — adopt the skeleton template's LAYER / STYLE / DIMSTYLE /
    // BLOCK_RECORD table heads (`5/<hex> 330/0 100/AcDbSymbolTable 70/<count>`)
    // so the from-scratch export re-emits a conformant head per table instead of
    // a head-less one (ezdxf would otherwise silently repair it → AutoCAD
    // recovery). The template's head handles (`1/5/4/9`…) are LOW-range and free
    // in the from-scratch handle space (the typed-table import strips them), and
    // were seeded into the entity minter above so nothing aliases them. The
    // writer re-emits the captured head but recomputes `70/<count>` from the
    // actual emitted record count, so a from-scratch doc whose record set differs
    // from the template's still emits a correct count.
    ...(skeleton.symbolTableHeaders
      ? { symbolTableHeaders: structuredClone(skeleton.symbolTableHeaders) }
      : {}),
    // FIX-110 — adopt the skeleton template's LTYPE table head (handle `2`) too.
    // The from-scratch LTYPE table is synthesised from the seeded standard
    // linetypes with NO captured header, so without this it would emit a
    // head-less LTYPE table that ezdxf silently repairs. The writer recomputes
    // `70/<count>` from the actual emitted (reference-filtered) linetype count,
    // so the adopted template count never desyncs from the emitted records.
    ...(skeleton.linetypeTableHeader
      ? { linetypeTableHeader: structuredClone(skeleton.linetypeTableHeader) }
      : {}),
    rawTableGroups,
    rawObjectGroups,
    rawPassthroughSections,
    // FIX C — the raw ENTITIES-section children with reminted own/sibling handles
    // (block raw children were mutated in place on `finalBlocks`). Only set when
    // the source carried any, mirroring the importer's optional-when-empty shape.
    ...(rawDocGroups.length > 0
      ? { rawEntityGroups: rawDocGroups as RawEntityGroup[] }
      : {}),
  };
}

/**
 * T9b — stamp the OWN records an emitted entity drags along, on its reminted
 * clone (`next`), AFTER the generic reference rewrite so neither is re-touched:
 *   - a DIMENSION's anonymous `*D<n>` block name (allocated by
 *     `assignAnonymousDimensionBlocks`, keyed by the SOURCE entity object);
 *   - a COMPLEX ENTITY's terminating SEQEND own group-5 — ALWAYS a fresh mint
 *     on the from-scratch path (the SEQEND is a writer-emitted terminator; a
 *     copied source handle carries no identity worth keeping and could alias a
 *     skeleton handle). Which entities have one is
 *     {@link emitsTerminatingSeqend}'s single declaration: an INSERT with
 *     attributes and every heavy POLYLINE. An INSERT without attributes emits
 *     no SEQEND, so it gets none.
 */
function stampEntityOwnRecords(
  next: object,
  source: object,
  anonymousDimBlocks: Pick<AnonymousDimensionBlockAssignment, "nameByEntity">,
  mintHandle: () => string,
): void {
  const clone = next as {
    type?: string;
    anonymousBlockName?: string;
    attributes?: unknown[];
    seqendHandle?: string;
  };
  // Consumed PER OCCURRENCE in visit order (T9b review round 1 P2) — the same
  // source object at two positions takes two distinct `*D<n>` names.
  const anonymousName = takeAnonymousDimensionBlockName(anonymousDimBlocks, source);
  if (anonymousName !== undefined) clone.anonymousBlockName = anonymousName;
  // WHICH entities emit a terminating SEQEND is declared ONCE, in
  // `entityRecordIdentity.ts`, and read by this from-scratch pass, the
  // imported-document export guard (`dxfBridge.newRecordHandles.ts`) and the
  // reducer clone helper alike (@codex PR #785 round 3, P1): an INSERT only when
  // it carries attributes, a heavy POLYLINE always (`dxf-io/writer.ts` emits
  // `POLYLINE … VERTEX* SEQEND` unconditionally). Before that, this site's own
  // inline `type === "Block"` test DELETED the heavy polyline's `seqendHandle`,
  // so a from-scratch drawing shipped a handle-less SEQEND that ezdxf silently
  // re-minted.
  if (emitsTerminatingSeqend(clone)) {
    clone.seqendHandle = mintHandle();
  } else if (clone.seqendHandle !== undefined) {
    delete clone.seqendHandle; // no SEQEND is emitted → no own handle to carry
  }
}

/**
 * Substitute every REFERENCE handle STRING on an entity graph whose lowercased
 * value is a key of `remap` with its remapped value, to FOLLOW a RECORD that the
 * record-own minter reminted (rare) from the entity refs that targeted its old
 * handle. Delegates to the SINGLE SOURCE OF TRUTH {@link remapEntityHandleReferences}
 * (round-16 BUG 1) — a `*Handle` reference-key value, a `*Handles` string-array
 * MEMBER, a structural `{code, handle}` pointer entry's `handle` (a numeric `code`
 * distinguishes it from an ATTRIB attribute's own handle), or a `{code:1005, value}`
 * XData handle. The bare own `handle` group-5 identity is DELIBERATELY EXCLUDED by
 * that helper (it was minted UNIQUELY in the remint pass; re-stamping it to a
 * colliding record's NEW handle would emit a duplicate group-5). Mutates in place.
 */
function applyHandleRemap(
  node: unknown,
  remap: ReadonlyMap<string, string>,
): void {
  remapEntityHandleReferences(node, remap);
}
